import Box from "@mui/material/Box";
import {NewLogoIconWithText} from "../NewHome/HomeIcons";
import {Button, Collapse, DialogContent, Radio, RadioGroup, FormControlLabel, TextField, Typography} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import React, {useContext, useEffect, useState} from "react";
import Dialog from "@mui/material/Dialog";
import {AuthContext} from "../../context/AuthContext";
import V2LoginForm from "./V2LoginForm";
import V2SignupMessage from "./V2SignupMessage";
import V2SignupForm from "./V2SignupForm";
import {useLocation, useNavigate} from "react-router-dom";
import { trackLandingEvent } from "../../analytics/landingEvents";
import { BASE_URL } from "../../context/FetchContext";
import {LibrettoPlan} from "../../utils/utils";
import Hls from "hls.js";

export type SpeechEditorLandingAppearance = "light" | "dark" | "beige";

const LANDING_APPEARANCE_VARS: Record<SpeechEditorLandingAppearance, Record<string, string>> = {
    light: {
        "--landing-canvas": "#FFFFFF",
        "--landing-surface": "#FFFFFF",
        "--landing-surface-muted": "#F6F5F4",
        "--landing-surface-soft": "#FAFAFA",
        "--landing-media-surface": "#F5F5F4",
        "--landing-text-primary": "#000000",
        "--landing-text-70": "rgba(0,0,0,0.7)",
        "--landing-text-60": "rgba(0,0,0,0.6)",
        "--landing-text-55": "rgba(0,0,0,0.55)",
        "--landing-text-50": "rgba(0,0,0,0.5)",
        "--landing-text-45": "rgba(0,0,0,0.45)",
        "--landing-text-42": "rgba(0,0,0,0.42)",
        "--landing-text-40": "rgba(0,0,0,0.4)",
        "--landing-text-35": "rgba(0,0,0,0.35)",
        "--landing-text-30": "rgba(0,0,0,0.3)",
        "--landing-action-bg": "#000000",
        "--landing-action-text": "#FFFFFF",
        "--landing-action-hover": "#222222",
        "--landing-header-bg": "rgba(255,255,255,0.85)",
        "--landing-border": "rgba(0,0,0,0.12)",
        "--landing-border-subtle": "rgba(0,0,0,0.07)",
        "--landing-divider": "#E8DDD4",
        "--landing-focus": "rgba(0,0,0,0.3)",
        "--landing-media-shadow": "inset 0 0 0 1px rgba(0,0,0,0.04), 0px 12px 30px rgba(0,0,0,0.08)",
        "--landing-card-shadow": "0px 8px 24px rgba(0,0,0,0.06)",
        "--landing-hero-background": "transparent",
    },
    beige: {
        "--landing-canvas": "#FBF6EC",
        "--landing-surface": "#FBF6EC",
        "--landing-surface-muted": "#EFE4D2",
        "--landing-surface-soft": "#FBF6EC",
        "--landing-media-surface": "#FBF6EC",
        "--landing-text-primary": "#2C241B",
        "--landing-text-70": "rgba(44,36,27,0.82)",
        "--landing-text-60": "rgba(44,36,27,0.76)",
        "--landing-text-55": "rgba(44,36,27,0.72)",
        "--landing-text-50": "rgba(44,36,27,0.69)",
        "--landing-text-45": "rgba(44,36,27,0.66)",
        "--landing-text-42": "rgba(44,36,27,0.64)",
        "--landing-text-40": "rgba(44,36,27,0.64)",
        "--landing-text-35": "rgba(44,36,27,0.64)",
        "--landing-text-30": "rgba(44,36,27,0.64)",
        "--landing-action-bg": "#2B241D",
        "--landing-action-text": "#FFF9EE",
        "--landing-action-hover": "#43372C",
        "--landing-header-bg": "#EFE4D2",
        "--landing-border": "rgba(69,50,31,0.16)",
        "--landing-border-subtle": "rgba(69,50,31,0.09)",
        "--landing-divider": "rgba(94,66,38,0.18)",
        "--landing-focus": "#9B672A",
        "--landing-media-shadow": "inset 0 0 0 1px rgba(255,255,255,0.34), 0 22px 54px rgba(83,57,31,0.17), 0 0 38px rgba(244,185,64,0.08)",
        "--landing-card-shadow": "0 16px 38px rgba(83,57,31,0.14)",
        "--landing-hero-background": "transparent",
    },
    dark: {
        "--landing-canvas": "#080A0F",
        "--landing-surface": "#0D1118",
        "--landing-surface-muted": "#151A24",
        "--landing-surface-soft": "#111620",
        "--landing-media-surface": "#07090D",
        "--landing-text-primary": "#F4F7FB",
        "--landing-text-70": "rgba(244,247,251,0.72)",
        "--landing-text-60": "rgba(244,247,251,0.64)",
        "--landing-text-55": "rgba(244,247,251,0.58)",
        "--landing-text-50": "rgba(244,247,251,0.52)",
        "--landing-text-45": "rgba(244,247,251,0.48)",
        "--landing-text-42": "rgba(244,247,251,0.44)",
        "--landing-text-40": "rgba(244,247,251,0.42)",
        "--landing-text-35": "rgba(244,247,251,0.38)",
        "--landing-text-30": "rgba(244,247,251,0.34)",
        "--landing-action-bg": "#F4F7FB",
        "--landing-action-text": "#080A0F",
        "--landing-action-hover": "#DDE5EE",
        "--landing-header-bg": "rgba(8,10,15,0.82)",
        "--landing-border": "rgba(255,255,255,0.13)",
        "--landing-border-subtle": "rgba(255,255,255,0.08)",
        "--landing-divider": "rgba(255,255,255,0.1)",
        "--landing-focus": "#34D399",
        "--landing-media-shadow": "inset 0 0 0 1px rgba(255,255,255,0.05), 0 24px 70px rgba(0,0,0,0.5), 0 0 42px rgba(52,211,153,0.055)",
        "--landing-card-shadow": "0 18px 50px rgba(0,0,0,0.32)",
        "--landing-hero-background": "radial-gradient(circle at 32% 18%, rgba(52,211,153,0.11), transparent 34%), radial-gradient(circle at 68% 20%, rgba(59,130,246,0.13), transparent 36%)",
    },
};

const defaultSignupPlan = LibrettoPlan.StudioFree;
const studioProSignupPlan = LibrettoPlan.StudioProMonthly;
const signupPlanQueryValueByPlan: Record<number, string> = {
    [LibrettoPlan.StudioFree]: "free",
    [LibrettoPlan.StudioMonthly]: "studio",
    [LibrettoPlan.StudioAnnual]: "studio-annual",
    [LibrettoPlan.StudioLifetime]: "studio-lifetime",
    [LibrettoPlan.StudioProMonthly]: "studio-pro",
};

const signupPlanByQueryValue: Record<string, number> = {
    free: LibrettoPlan.StudioFree,
    studio: LibrettoPlan.StudioMonthly,
    "studio-annual": LibrettoPlan.StudioAnnual,
    "studio-lifetime": LibrettoPlan.StudioLifetime,
    "studio-pro": LibrettoPlan.StudioProMonthly,
};

const resolveSignupPlanFromOptions = (options?: any) => {
    if (!options || typeof options !== "object") {
        return defaultSignupPlan;
    }
    const rawPlan = options.librettoPlan ?? options.LibrettoPlan ?? options.plan;
    const parsedPlan = typeof rawPlan === "string" && rawPlan.trim() !== ""
        ? Number(rawPlan)
        : rawPlan;
    return Object.prototype.hasOwnProperty.call(signupPlanQueryValueByPlan, parsedPlan)
        ? parsedPlan
        : defaultSignupPlan;
};

const signupPlanQueryValueForPlan = (plan: number) => (
    signupPlanQueryValueByPlan[plan] || signupPlanQueryValueByPlan[defaultSignupPlan]
);

const resolveSignupPlanFromSearch = (search: string) => {
    const queryValue = String(new URLSearchParams(search).get("plan") || "").trim().toLowerCase();
    return signupPlanByQueryValue[queryValue] ?? defaultSignupPlan;
};


const V2TopBarNavLink = ({label, href}) => {
    return (
        <Button
            component="a"
            href={href}
            variant="text"
            disableRipple
            sx={{
                minWidth: 'auto',
                padding: 0,
                textTransform: 'uppercase',
                fontFamily: '"Space Mono", monospace',
                fontWeight: 400,
                fontSize: '15px',
                lineHeight: '22px',
                letterSpacing: '-1px',
                color: 'var(--landing-text-primary, black)',
                opacity: 0.9,
                '&:hover': {
                    backgroundColor: 'transparent',
                    opacity: 0.7,
                },
            }}
        >
            {label}
        </Button>
    )
}

const V2SigninButton = ({onClick}) => {
    return (
        <Button
            variant="text"
            disableRipple
            sx={{
                backgroundColor: 'transparent',
                color: 'var(--landing-text-primary, black)',
                height: '36px',
                borderRadius: '999px',
                padding: '8px 14px',
                textTransform: 'uppercase',
                fontFamily: '"Space Mono", monospace',
                fontWeight: 400,
                fontSize: '15px',
                lineHeight: '22px',
                letterSpacing: '-1px',
                whiteSpace: 'nowrap',
                zIndex: 1,
                border: '1px solid transparent',
                '&:hover': {
                    backgroundColor: 'transparent',
                    borderColor: 'var(--landing-border, rgba(0,0,0,0.18))',
                    opacity: 0.75,
                },
            }}
            onClick={onClick}
        >
            Sign In
        </Button>
    )
}

const V2GetStartedButton = ({onClick}) => {
    return (
        <Button
            variant="contained"
            sx={{
                backgroundColor: 'var(--landing-action-bg, black)',
                color: 'var(--landing-action-text, white)',
                height: '30px',
                borderRadius: '10px',
                padding: '13px 14px',
                textTransform: 'uppercase',
                fontFamily: '"Space Mono", monospace',
                fontWeight: 700,
                fontSize: '14px',
                lineHeight: '22px',
                letterSpacing: '-0.8px',
                boxShadow: '0px 1px 4px 0px rgba(0,0,0,0.25)',
                whiteSpace: 'nowrap',
                zIndex: 1,
                '&:hover': {
                    backgroundColor: 'var(--landing-action-hover, #333)',
                },
            }}
            onClick={onClick}
        >
            Get Started
        </Button>
    )
}

const OrangeFloatingPlaybackIcon = () => {
    return (
        <svg width="95" height="95" viewBox="0 0 95 95" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="94.9984" height="94.9984" rx="47.4992" fill="#FBEAD6"/>
            <path d="M36 36.0002H58" stroke="#ED6F39" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M39.668 30.5002H54.3346" stroke="#ED6F39" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round"/>
            <path fillRule="evenodd" clipRule="evenodd" d="M45.6434 48.9637L50.6246 51.9099C51.2552 52.282 51.2552 53.195 50.6246 53.5672L45.6434 56.5134C45.0017 56.8929 44.1914 56.4309 44.1914 55.6847V49.7942C44.1896 49.0462 45.0017 48.5842 45.6434 48.9637V48.9637Z" stroke="#ED6F39" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round"/>
            <path fillRule="evenodd" clipRule="evenodd" d="M59.8333 63.5H34.1667C32.1408 63.5 30.5 61.8592 30.5 59.8333V45.1667C30.5 43.1408 32.1408 41.5 34.1667 41.5H59.8333C61.8592 41.5 63.5 43.1408 63.5 45.1667V59.8333C63.5 61.8592 61.8592 63.5 59.8333 63.5Z" stroke="#ED6F39" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    )
}

const YellowFloatingCamIcon = () => {
    return (
        <svg width="84" height="84" viewBox="0 0 84 84" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="83.4732" height="83.4732" rx="41.7366" fill="#FDF4CF"/>
            <path d="M48.6641 39.1541L54.4561 35.5839C54.9703 35.2669 55.6157 35.2529 56.1432 35.5474C56.6706 35.8418 56.9974 36.3986 56.9974 37.0026V48.664C56.9974 49.268 56.6706 49.8248 56.1432 50.1192C55.6158 50.4137 54.9704 50.3997 54.4562 50.0828L48.6641 46.5124" stroke="#F4B940" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            <rect x="27" y="32" width="21.6667" height="21.6667" rx="5" stroke="#F4B940" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    )
}

const RedTimelineCutIcon = () => {
    return (
        <svg width="99" height="99" viewBox="0 0 99 99" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="98.6421" height="98.6421" rx="49.321" fill="#FCECE4"/>
            <path d="M60 43.4455H67.3333" stroke="#EB4241" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M60 54.5002H67.3333" stroke="#EB4241" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M48.9987 52.6665V56.3332" stroke="#EB4241" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M48.9987 42.3262V45.9928" stroke="#EB4241" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M38.0013 54.5544H30.668" stroke="#EB4241" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M38.0013 43.5002H30.668" stroke="#EB4241" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M41.668 63.6668H34.793C33.6989 63.6671 32.6495 63.2325 31.8759 62.4589C31.1023 61.6853 30.6677 60.6359 30.668 59.5418V38.4585C30.6677 37.3644 31.1023 36.3151 31.8759 35.5414C32.6495 34.7678 33.6989 34.3333 34.793 34.3335H41.668" stroke="#EB4241" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M56.332 34.3335H63.207C64.3011 34.3333 65.3505 34.7678 66.1241 35.5414C66.8977 36.3151 67.3323 37.3644 67.332 38.4585V59.5418C67.3323 60.6359 66.8977 61.6853 66.1241 62.4589C65.3505 63.2325 64.3011 63.6671 63.207 63.6668H56.332" stroke="#EB4241" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M59.9987 34.3335V63.6668" stroke="#EB4241" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M38.2214 34.3335V63.6668" stroke="#EB4241" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round"/>
            <path fillRule="evenodd" clipRule="evenodd" d="M51.7421 67.3332H46.25C45.9218 67.3361 45.6174 67.1619 45.4535 66.8775C45.2897 66.593 45.2917 66.2424 45.4588 65.9598L48.2048 61.3633C48.3721 61.086 48.6722 60.9165 48.996 60.9165C49.3199 60.9165 49.62 61.086 49.7873 61.3633L52.5333 65.9598C52.7004 66.2424 52.7024 66.593 52.5386 66.8775C52.3747 67.1619 52.0703 67.3361 51.7421 67.3332V67.3332Z" stroke="#EB4241" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round"/>
            <path fillRule="evenodd" clipRule="evenodd" d="M46.25 30.6665H51.7421C52.0703 30.6637 52.3747 30.8378 52.5386 31.1222C52.7024 31.4067 52.7004 31.7573 52.5333 32.0399L49.7873 36.6364C49.62 36.9137 49.3199 37.0832 48.996 37.0832C48.6722 37.0832 48.3721 36.9137 48.2048 36.6364L45.4588 32.0399C45.2917 31.7573 45.2897 31.4067 45.4535 31.1222C45.6174 30.8378 45.9218 30.6637 46.25 30.6665Z" stroke="#EB4241" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    )
}

const LightBlueFloatingIcon = () => {
    return (
        <svg width="82" height="82" viewBox="0 0 82 82" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="81.2802" height="81.2802" rx="40.6401" fill="#E5F8FE"/>
            <path fillRule="evenodd" clipRule="evenodd" d="M26.75 42.5833V31.5C26.75 28.8766 28.8766 26.75 31.5 26.75H50.5C53.1234 26.75 55.25 28.8766 55.25 31.5V42.5833C55.25 45.2067 53.1234 47.3333 50.5 47.3333H31.5C28.8766 47.3333 26.75 45.2067 26.75 42.5833Z" stroke="#4A9EE4" strokeWidth="2.375" strokeLinecap="round" strokeLinejoin="round"/>
            <path fillRule="evenodd" clipRule="evenodd" d="M38.0249 39.9695V34.1141C38.025 33.7463 38.2213 33.4065 38.5399 33.2227C38.8585 33.0389 39.2509 33.0391 39.5693 33.2231L44.6357 36.1508C44.9539 36.3347 45.1499 36.6743 45.1499 37.0418C45.1499 37.4093 44.9539 37.7489 44.6357 37.9327L39.5694 40.8605C39.2509 41.0445 38.8585 41.0447 38.5399 40.8609C38.2213 40.6771 38.025 40.3373 38.0249 39.9695V39.9695Z" stroke="#4A9EE4" strokeWidth="2.375" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M53.6668 53.6667H37.8335" stroke="#4A9EE4" strokeWidth="2.375" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M28.3335 53.6667H33.0835" stroke="#4A9EE4" strokeWidth="2.375" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M33.0832 55.2502V52.0835" stroke="#4A9EE4" strokeWidth="2.375" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>

    )
}

const PurpleFloatingFilmIcon = () => {
    return (
        <svg width="79" height="79" viewBox="0 0 79 79" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="79" height="79" rx="39.5" fill="#F4E6FD"/>
            <path d="M32.5 26.3594V53.3594" stroke="#9A64F6" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M47.5 53.3594V26.3594" stroke="#9A64F6" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round"/>
            <path fillRule="evenodd" clipRule="evenodd" d="M50.5 53.3594H29.5C27.8425 53.3594 26.5 52.0169 26.5 50.3594V29.3594C26.5 27.7019 27.8425 26.3594 29.5 26.3594H50.5C52.1575 26.3594 53.5 27.7019 53.5 29.3594V50.3594C53.5 52.0169 52.1575 53.3594 50.5 53.3594Z" stroke="#9A64F6" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M32.5 32.3594H26.5" stroke="#9A64F6" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M32.5 47.3594H26.5" stroke="#9A64F6" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M47.5 47.3594H53.5" stroke="#9A64F6" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M47.5 32.3594H53.5" stroke="#9A64F6" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M53.5 39.8594H26.5" stroke="#9A64F6" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    )
}

const CyanFloatingIcon = () => {
    return (
        <svg width="76" height="76" viewBox="0 0 76 76" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="75.1664" height="75.1664" rx="37.5832" fill="#E3FBFA"/>
            <path d="M47.5459 28.4541C52.818 33.7261 52.818 42.2739 47.5459 47.5459C42.2738 52.818 33.7261 52.818 28.4541 47.5459C23.182 42.2738 23.182 33.7261 28.4541 28.4541C33.7261 23.182 42.2739 23.182 47.5459 28.4541" stroke="#57BEB9" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M38 51.5H51.5" stroke="#57BEB9" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M38 45.5V47" stroke="#57BEB9" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M38 29V30.5" stroke="#57BEB9" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M30.5 38H29" stroke="#57BEB9" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M47 38H45.5" stroke="#57BEB9" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M43.3109 43.31L44.3609 44.36" stroke="#57BEB9" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M31.6391 31.64L32.6891 32.69" stroke="#57BEB9" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M32.6891 43.31L31.6391 44.36" stroke="#57BEB9" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M44.3609 31.64L43.3109 32.69" stroke="#57BEB9" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M40.1213 35.8787C41.2929 37.0503 41.2929 38.9498 40.1213 40.1213C38.9498 41.2929 37.0503 41.2929 35.8787 40.1213C34.7071 38.9498 34.7071 37.0503 35.8787 35.8787C37.0503 34.7071 38.9498 34.7071 40.1213 35.8787" stroke="#57BEB9" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    )
}

const useDelayedShow = (isActive: boolean, delayMs = 1500) => {
    const [show, setShow] = useState(false);
    const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    React.useEffect(() => {
        if (isActive) {
            timerRef.current = setTimeout(() => setShow(true), delayMs);
        } else {
            setShow(false);
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
        }
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [isActive, delayMs]);

    return show;
};

// Video placeholder component for Mux playback IDs or direct URLs.
const VideoPlaceholder = ({
                              aspectRatio = '16/10',
                              playbackId,
                              videoSrc,
                              posterSrc,
                              autoPlay = true,
                              loop = true,
                              isLarge = false,
                              onTimeUpdate,
                              videoRef,
                              sx,
                          }: {
    aspectRatio?: string;
    playbackId?: string;
    videoSrc?: string;
    posterSrc?: string;
    autoPlay?: boolean;
    loop?: boolean;
    isLarge?: boolean;
    onTimeUpdate?: (currentTime: number) => void;
    videoRef?: React.MutableRefObject<HTMLVideoElement | null>;
    sx?: any;
}) => {
    const localVideoRef = React.useRef<HTMLVideoElement>(null);
    const resolvedVideoRef = videoRef ?? localVideoRef;
    const hlsRef = React.useRef<Hls | null>(null);

    const isDirectUrl = !!videoSrc;
    const muxVideoUrl = playbackId ? `https://stream.mux.com/${playbackId}.m3u8?rendition_order=desc` : '';
    const resolvedVideoUrl = videoSrc || muxVideoUrl;
    const height = isLarge ? "1976" : "720";
    const width = isLarge ? "3448" : "1280";
    const resolvedPosterUrl = posterSrc || (playbackId
        ? `https://image.mux.com/${playbackId}/thumbnail.png?time=0.5&width=${width}&height=${height}&fit_mode=preserve`
        : '');

    React.useEffect(() => {
        const video = resolvedVideoRef.current;
        if (!video || !resolvedVideoUrl) return;

        // Direct URL (mp4, webm, etc.): set src directly, no HLS needed
        if (isDirectUrl) {
            video.src = resolvedVideoUrl;
            return () => {
                video.pause();
            };
        }

        // Mux HLS stream
        if (video.canPlayType('application/vnd.apple.mpegurl')) {
            video.src = resolvedVideoUrl;
        } else if (Hls.isSupported()) {
            const hls = new Hls({
                startLevel: -1,            // let HLS.js pick based on manifest order (desc = highest first)
                capLevelToPlayerSize: true, // don't load higher res than the player element needs
                maxMaxBufferLength: 30,
                abrEwmaDefaultEstimate: 5_000_000, // assume 5 Mbps and start with high quality
            });
            hlsRef.current = hls;
            hls.loadSource(resolvedVideoUrl);
            hls.attachMedia(video);
        } else {
            video.src = resolvedVideoUrl;
        }

        return () => {
            video.pause();
            if (hlsRef.current) {
                hlsRef.current.destroy();
                hlsRef.current = null;
            }
        };
    }, [resolvedVideoRef, resolvedVideoUrl, isDirectUrl]);

    // Handles autoPlay toggling (scroll-based play/pause)
    React.useEffect(() => {
        const video = resolvedVideoRef.current;
        if (!video) return;

        const tryPlay = () => {
            if (!autoPlay) return;
            video.play().catch(() => {});
        };

        if (!autoPlay) {
            video.pause();
            return;
        }

        video.addEventListener('canplay', tryPlay);
        tryPlay();

        return () => {
            video.removeEventListener('canplay', tryPlay);
        };
    }, [autoPlay, resolvedVideoRef]);

    // Delayed loop: pause at end, wait, then restart
    const loopTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    React.useEffect(() => {
        const video = resolvedVideoRef.current;
        if (!video || !loop) return;

        const handleEnded = () => {
            loopTimerRef.current = setTimeout(() => {
                video.currentTime = 0;
                if (autoPlay) {
                    video.play().catch(() => {});
                }
            }, 4000);
        };

        video.addEventListener('ended', handleEnded);
        return () => {
            video.removeEventListener('ended', handleEnded);
            if (loopTimerRef.current) {
                clearTimeout(loopTimerRef.current);
                loopTimerRef.current = null;
            }
        };
    }, [loop, autoPlay, resolvedVideoRef]);

    const hasVideo = !!playbackId || !!videoSrc;

    return (
        <Box
            sx={{
                position: 'relative',
                width: '100%',
                aspectRatio,
                backgroundColor: 'var(--landing-media-surface, #F5F5F4)',
                borderRadius: '12px',
                border: '1px solid var(--landing-border, #E8E8E8)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                flexShrink: 0,
                minHeight: 0,
                ...(sx || {}),
            }}
        >
            {hasVideo ? (
                <video
                    ref={resolvedVideoRef}
                    muted
                    playsInline
                    autoPlay={autoPlay}
                    preload={autoPlay ? 'auto' : 'metadata'}
                    poster={resolvedPosterUrl || undefined}
                    onTimeUpdate={(event) => onTimeUpdate?.(event.currentTarget.currentTime)}
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                    }}
                />
            ) : (
                <Typography
                    sx={{
                        fontFamily: '"Inter", sans-serif',
                        fontSize: '14px',
                        color: 'var(--landing-text-45, #9CA3AF)',
                        textAlign: 'center',
                        px: 2,
                    }}
                >
                    Video placeholder
                </Typography>
            )}
        </Box>
    );
};

const SINGLE_HERO_VIDEO_ID = 'lM2LZte1m2MiBts1rrj0146Ray64zc4vavoI91wv8Pz4';
const SECTION_TWO_VIDEO_ID = '3ohWY6G66Uv5F202a00WLuAMEZtU302PvsNth8O02Q4ptBM';
const SECTION_THREE_VIDEO_ID = 'E8YcCs2m3Q9rUBvp1g5nMD5qA3kH4VyOiK98xHc7qVI';

const HeroSection = ({isMobile, handleLoginFormOpen, handleSignupFormOpen}) => {

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: { xs: '16px', md: '20px' },
                alignItems: 'center',
                position: 'relative',
                width: '100%',
                height: '100%',
                pt: { xs: '32px', md: '56px' },
                pb: { xs: '16px', md: '24px' },
                background: 'var(--landing-hero-background, transparent)',
            }}
        >
            {/* Headline */}
            <Typography
                sx={{
                    fontFamily: 'Arial, sans-serif',
                    fontSize: { xs: '42px', sm: '64px', md: '84px' },
                    lineHeight: { xs: 1.05, md: 1.05 },
                    letterSpacing: { xs: '-2px', sm: '-3px', md: '-4px' },
                    color: 'var(--landing-text-primary, black)',
                    textAlign: 'center',
                    maxWidth: '1000px',
                    width: '100%',
                    zIndex: 1,
                }}
            >
                {isMobile ? 'Record and shape' : 'Record your conversation.'}
                <br />
                <Box
                    component="span"
                    sx={{
                        background: 'linear-gradient(135deg, #34D399 0%, #10B981 45%, #3B82F6 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                    }}
                >
                    {isMobile ? 'your episode.' : 'Shape your episode.'}
                </Box>
            </Typography>

            {/* Subtitle */}
            <Typography
                sx={{
                    fontFamily: '"Inter", sans-serif',
                    fontWeight: 400,
                    fontSize: { xs: '16px', md: '19px' },
                    lineHeight: { xs: 1.55, md: 1.6 },
                    color: 'var(--landing-text-55, rgba(0,0,0,0.55))',
                    textAlign: 'center',
                    maxWidth: '950px',
                    width: '100%',
                    zIndex: 1,
                    px: 2,
                }}
            >
                {isMobile
                    ? 'Record solo or with guests, edit by transcript, and polish your episode in one workspace.'
                    : 'Record solo or with guests in up to 4K, then edit your episode through the transcript and polish the sound in the same workspace.'}
            </Typography>

            {/* CTA Button */}
            <Button
                variant="contained"
                sx={{
                    backgroundColor: 'var(--landing-action-bg, black)',
                    color: 'var(--landing-action-text, white)',
                    height: { xs: '56px', md: '60px' },
                    borderRadius: '14px',
                    padding: { xs: '16px 42px', md: '18px 52px' },
                    textTransform: 'none',
                    fontFamily: '"Inter", sans-serif',
                    fontWeight: 600,
                    fontSize: { xs: '17px', md: '18px' },
                    letterSpacing: '-0.3px',
                    boxShadow: '0px 2px 8px rgba(0,0,0,0.2)',
                    whiteSpace: 'nowrap',
                    zIndex: 1,
                    transition: 'background-color 200ms ease, transform 200ms ease, box-shadow 200ms ease',
                    '&:hover': {
                        backgroundColor: 'var(--landing-action-hover, #222)',
                        transform: 'translateY(-1px)',
                        boxShadow: '0px 4px 16px rgba(0,0,0,0.25)',
                    },
                }}
                onClick={(e) => {
                    handleSignupFormOpen(e);
                    trackLandingEvent("landing_cta_click_hero_get_started");
                }}
            >
                Create your account
            </Button>

            <Typography
                sx={{
                    fontFamily: '"Inter", sans-serif',
                    fontWeight: 400,
                    fontSize: '13px',
                    lineHeight: 1.5,
                    color: 'var(--landing-text-42, rgba(0,0,0,0.42))',
                    textAlign: 'center',
                    zIndex: 1,
                }}
            >
                No credit card required
            </Typography>
        </Box>
    );
}

const HowItWorksRecordingIcon = () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
        <rect x="3" y="6" width="18" height="12" rx="3" stroke="currentColor" strokeWidth="1.5" />
        <path d="M10.25 9.5V14.5L14.25 12L10.25 9.5Z" fill="currentColor" />
    </svg>
);

const HowItWorksEditingIcon = () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
        <path
            d="M4 20H6.5L18.2 8.3C18.6 7.9 18.6 7.3 18.2 6.9L17.1 5.8C16.7 5.4 16.1 5.4 15.7 5.8L4 17.5V20Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinejoin="round"
        />
        <path d="M13.8 7.7L16.3 10.2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
);

const HowItWorksPublishingIcon = () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
        <path d="M12 3V13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M8 7L12 3L16 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path
            d="M4 13.5V20C4 20.55 4.45 21 5 21H19C19.55 21 20 20.55 20 20V13.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
        />
    </svg>
);

const HOW_IT_WORKS_STAGES: Array<{
    title: string;
    description: string;
    startTime: number;
    icon: React.ReactNode;
}> = [
    {
        title: "Upload or Record",
        description: "Bring in your recording from any source, or record on here directly.",
        startTime: 0,
        icon: <HowItWorksRecordingIcon />,
    },
    {
        title: "Automatic Cleanup",
        description: "Remove filler speech, long pauses and background noise automatically.",
        startTime: 5,
        icon: <HowItWorksEditingIcon />,
    },
    {
        title: "Review & Export",
        description: "Review, make more edits and then export your final version.",
        startTime: 15,
        icon: <HowItWorksPublishingIcon />,
    },
];

const getHowItWorksStageIndex = (currentTime: number) => {
    if (currentTime < HOW_IT_WORKS_STAGES[1].startTime) return 0;
    if (currentTime < HOW_IT_WORKS_STAGES[2].startTime) return 1;
    return 2;
};

const HOW_IT_WORKS_VIDEO_SECTIONS = {
    primary: 'how-it-works-main',
    secondary: 'how-it-works-secondary',
    tertiary: 'how-it-works-tertiary',
    productDemo: 'product-demo',
} as const;

const HOW_IT_WORKS_ANCHORS = {
    cleanup: "how-it-works-cleanup",
    editByTranscript: "how-it-works-edit-by-transcript",
    fixZoomRecording: "how-it-works-fix-zoom-recording",
} as const;

type HowItWorksVideoSectionId =
    | typeof HOW_IT_WORKS_VIDEO_SECTIONS.primary
    | typeof HOW_IT_WORKS_VIDEO_SECTIONS.secondary
    | typeof HOW_IT_WORKS_VIDEO_SECTIONS.tertiary
    | typeof HOW_IT_WORKS_VIDEO_SECTIONS.productDemo;

// Section 1: How it works
const HowItWorksSection = ({shouldPlayVideo, handleSignupFormOpen}: {shouldPlayVideo: boolean; handleSignupFormOpen: (e?: any) => void}) => {
    const [activeStageIndex, setActiveStageIndex] = useState(0);
    const videoRef = React.useRef<HTMLVideoElement | null>(null);
    const showCta = useDelayedShow(shouldPlayVideo, 2000);

    const handleVideoTimeUpdate = (currentTime: number) => {
        const nextStageIndex = getHowItWorksStageIndex(currentTime);
        setActiveStageIndex((prevStageIndex) =>
            prevStageIndex === nextStageIndex ? prevStageIndex : nextStageIndex
        );
    };

    const handleStageClick = (stageIndex: number) => {
        const video = videoRef.current;
        if (!video) return;

        const nextTime = HOW_IT_WORKS_STAGES[stageIndex].startTime;
        video.currentTime = nextTime;
        setActiveStageIndex(stageIndex);
        if (shouldPlayVideo) {
            video.play().catch(() => {});
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    mb: { xs: '28px', md: '40px' },
                }}
            >

                <Typography
                    sx={{
                        fontFamily: '"Hedvig Letters Serif", serif',
                        fontSize: { xs: '28px', md: '54px' },
                        lineHeight: { xs: '30px', md: '60px' },
                        letterSpacing: { xs: '-1.5px', md: '-2.5px' },
                        color: 'var(--landing-text-primary, black)',
                        mb: { xs: 2, md: 2.5 },
                        maxWidth: { xs: '277px', md: 'none' },
                    }}
                >
                    From raw recording to publish-ready quickly
                </Typography>
                <Typography
                    sx={{
                        fontFamily: '"Inter", sans-serif',
                        fontWeight: 500,
                        fontSize: '16px',
                        lineHeight: '22px',
                        letterSpacing: '-0.5px',
                        color: 'var(--landing-text-primary, black)',
                        opacity: 0.7,
                        maxWidth: '327px',
                        display: { xs: 'block', md: 'none' },
                    }}
                >
                    Remove filler, pauses, and retakes automatically and export a clean version.
                </Typography>
                <Typography
                    sx={{
                        fontFamily: '"Inter", sans-serif',
                        fontWeight: 500,
                        fontSize: '18px',
                        lineHeight: '27px',
                        letterSpacing: '-0.5px',
                        color: 'var(--landing-text-primary, black)',
                        opacity: 0.7,
                        maxWidth: '700px',
                        display: 'block',
                    }}
                >
                    Remove filler speech, long pauses and retakes automatically from your recording, and export a publish-ready version.
                </Typography>
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: { xs: '16px', md: '24px' },
                    maxWidth: { xs: '343px', md: '980px' },
                    mx: 'auto',
                    width: '100%',
                }}
            >
                <Box
                    sx={{
                        borderRadius: { xs: '14px', md: '16px' },
                        border: '1px solid var(--landing-border, rgba(0,0,0,0.12))',
                        overflow: 'hidden',
                        backgroundColor: 'var(--landing-surface, white)',
                        boxShadow: 'var(--landing-media-shadow, inset 0 0 0 1px rgba(0,0,0,0.04), 0px 12px 30px rgba(0,0,0,0.08))',
                    }}
                >
                    <VideoPlaceholder
                        aspectRatio="16/9"
                        playbackId={SINGLE_HERO_VIDEO_ID}
                        autoPlay={shouldPlayVideo}
                        videoRef={videoRef}
                        onTimeUpdate={handleVideoTimeUpdate}
                        sx={{
                            backgroundColor: 'var(--landing-surface-muted, #F6F5F4)',
                            border: 'none',
                            borderRadius: 'inherit',
                        }}
                    />
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        mt: { xs: 2, md: 3 },
                        opacity: showCta ? 1 : 0,
                        transform: showCta ? 'translateY(0)' : 'translateY(6px)',
                        transition: 'opacity 500ms ease, transform 500ms ease',
                    }}
                >
                    <Button
                        variant="text"
                        disableRipple
                        onClick={(e) => {
                            handleSignupFormOpen(e);
                            trackLandingEvent("landing_cta_click_section1_try_free");
                        }}
                        sx={{
                            fontFamily: '"Space Mono", monospace',
                            fontWeight: 700,
                            fontSize: '14px',
                            letterSpacing: '-0.5px',
                            textTransform: 'uppercase',
                            color: 'var(--landing-text-primary, black)',
                            opacity: 0.7,
                            '&:hover': { opacity: 1, backgroundColor: 'transparent' },
                        }}
                    >
                        Create a free account &rarr;
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

const HowItWorksSectionTwo = ({shouldPlayVideo, handleSignupFormOpen}: {shouldPlayVideo: boolean; handleSignupFormOpen: (e?: any) => void}) => {
    const videoRef = React.useRef<HTMLVideoElement | null>(null);
    const showCta = useDelayedShow(shouldPlayVideo, 2000);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
            }}
        >
            {/* Mobile: centered header above video */}
            <Box
                sx={{
                    display: { xs: 'flex', md: 'none' },
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    mb: '28px',
                }}
            >

                <Typography
                    sx={{
                        fontFamily: '"Hedvig Letters Serif", serif',
                        fontSize: '28px',
                        lineHeight: '30px',
                        letterSpacing: '-1.5px',
                        color: 'var(--landing-text-primary, black)',
                        maxWidth: '277px',
                        mb: 2,
                    }}
                >
                    Edit by transcript
                </Typography>
                <Typography
                    sx={{
                        fontFamily: '"Inter", sans-serif',
                        fontWeight: 500,
                        fontSize: '16px',
                        lineHeight: '22px',
                        letterSpacing: '-0.5px',
                        color: 'var(--landing-text-primary, black)',
                        opacity: 0.7,
                        maxWidth: '327px',
                    }}
                >
                    Make precise edits quickly without scrubbing the timeline.
                </Typography>
            </Box>

            {/* Mobile: video */}
            <Box
                sx={{
                    display: { xs: 'flex', md: 'none' },
                    flexDirection: 'column',
                    maxWidth: '343px',
                    mx: 'auto',
                    width: '100%',
                }}
            >
                <Box
                    sx={{
                        borderRadius: '14px',
                        border: '1px solid var(--landing-border, rgba(0,0,0,0.12))',
                        overflow: 'hidden',
                        backgroundColor: 'var(--landing-surface, white)',
                        boxShadow: 'var(--landing-media-shadow, inset 0 0 0 1px rgba(0,0,0,0.04), 0px 12px 30px rgba(0,0,0,0.08))',
                    }}
                >
                    <VideoPlaceholder
                        aspectRatio="16/9"
                        playbackId={SECTION_TWO_VIDEO_ID}
                        autoPlay={shouldPlayVideo}
                        isLarge={true}
                        videoRef={videoRef}
                        sx={{
                            backgroundColor: 'var(--landing-surface-muted, #F6F5F4)',
                            border: 'none',
                            borderRadius: 'inherit',
                        }}
                    />
                </Box>
            </Box>

            {/* Desktop: side-by-side heading + video */}
            <Box
                sx={{
                    display: { xs: 'none', md: 'flex' },
                    flexDirection: 'row',
                    alignItems: 'stretch',
                    gap: { md: '40px', lg: '56px' },
                    maxWidth: '1240px',
                    mx: 'auto',
                    width: '100%',
                }}
            >
                {/* Heading + subheader side */}
                <Box
                    sx={{
                        flex: { md: '0 0 300px', lg: '0 0 340px' },
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        gap: '16px',
                    }}
                >
    
                    <Typography
                        sx={{
                            fontFamily: '"Hedvig Letters Serif", serif',
                            fontSize: { md: '40px', lg: '46px' },
                            lineHeight: { md: '44px', lg: '50px' },
                            letterSpacing: '-2px',
                            color: 'var(--landing-text-primary, black)',
                        }}
                    >
                        Edit by transcript
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: '"Inter", sans-serif',
                            fontWeight: 500,
                            fontSize: '17px',
                            lineHeight: '26px',
                            letterSpacing: '-0.5px',
                            color: 'var(--landing-text-primary, black)',
                            opacity: 0.65,
                        }}
                    >
                        Create scenes & make precise edits quickly without scrubbing the timeline.
                    </Typography>
                    <Box
                        sx={{
                            opacity: showCta ? 1 : 0,
                            transform: showCta ? 'translateY(0)' : 'translateY(6px)',
                            transition: 'opacity 500ms ease, transform 500ms ease',
                            mt: 0.5,
                        }}
                    >
                        <Button
                            variant="text"
                            disableRipple
                            onClick={(e) => {
                                handleSignupFormOpen(e);
                                trackLandingEvent("landing_cta_click_section2_try_free");
                            }}
                            sx={{
                                fontFamily: '"Space Mono", monospace',
                                fontWeight: 700,
                                fontSize: '14px',
                                letterSpacing: '-0.5px',
                                textTransform: 'uppercase',
                                color: 'var(--landing-text-primary, black)',
                                opacity: 0.7,
                                padding: 0,
                                minWidth: 'auto',
                                '&:hover': { opacity: 1, backgroundColor: 'transparent' },
                            }}
                        >
                            Create a free account &rarr;
                        </Button>
                    </Box>
                </Box>

                {/* Video side */}
                <Box
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        borderRadius: '16px',
                        border: '1px solid var(--landing-border, rgba(0,0,0,0.12))',
                        overflow: 'hidden',
                        backgroundColor: 'var(--landing-surface, white)',
                        boxShadow: 'var(--landing-media-shadow, inset 0 0 0 1px rgba(0,0,0,0.04), 0px 12px 30px rgba(0,0,0,0.08))',
                    }}
                >
                    <VideoPlaceholder
                        aspectRatio="16/10"
                        playbackId={SECTION_TWO_VIDEO_ID}
                        autoPlay={shouldPlayVideo}
                        isLarge={true}
                        videoRef={videoRef}
                        sx={{
                            backgroundColor: 'var(--landing-surface-muted, #F6F5F4)',
                            border: 'none',
                            borderRadius: 'inherit',
                        }}
                    />
                </Box>
            </Box>
        </Box>
    );
};

const HowItWorksSectionThree = ({shouldPlayVideo, handleSignupFormOpen}: {shouldPlayVideo: boolean; handleSignupFormOpen: (e?: any) => void}) => {
    const videoRef = React.useRef<HTMLVideoElement | null>(null);
    const showCta = useDelayedShow(shouldPlayVideo);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: '100%',
            }}
        >
            {/* Text above video */}
            <Box
                sx={{
                    mb: { xs: '20px', md: '28px' },
                    textAlign: 'center',
                    maxWidth: '1080px',
                }}
            >
                <Typography
                    sx={{
                        fontFamily: '"Hedvig Letters Serif", serif',
                        fontSize: { xs: '26px', md: '48px' },
                        lineHeight: { xs: '36px', md: '62px' },
                        letterSpacing: { xs: '-1.5px', md: '-2.5px' },
                        color: 'var(--landing-text-primary, black)',
                    }}
                >
                    Everything you need to edit your recordings faster
                </Typography>
                <Typography
                    sx={{
                        fontFamily: '"Inter", sans-serif',
                        fontWeight: 500,
                        fontSize: '17px',
                        lineHeight: '26px',
                        letterSpacing: '-0.5px',
                        color: 'var(--landing-text-primary, black)',
                        opacity: 0.55,
                        mt: 1.5,
                    }}
                >
                    Podcasts . Interviews . Lectures . Meetings . Any long-form recording.
                </Typography>
            </Box>

            {/* Desktop video */}
            <Box
                sx={{
                    display: { xs: 'none', md: 'block' },
                    width: '100%',
                    maxWidth: '1080px',
                    mx: 'auto',
                }}
            >
                <Box
                    sx={{
                        borderRadius: '16px',
                        border: '1px solid var(--landing-border, rgba(0,0,0,0.12))',
                        overflow: 'hidden',
                        backgroundColor: 'var(--landing-surface, white)',
                        boxShadow: 'var(--landing-media-shadow, inset 0 0 0 1px rgba(0,0,0,0.04), 0px 12px 30px rgba(0,0,0,0.08))',
                    }}
                >
                    <VideoPlaceholder
                        aspectRatio="16/9"
                        videoSrc="https://storage.googleapis.com/libretto-public-assets/videos/MySharpVideo.mp4"
                        posterSrc="https://image.mux.com/E8YcCs2m3Q9rUBvp1g5nMD5qA3kH4VyOiK98xHc7qVI/thumbnail.png?width=1280&height=720&fit_mode=preserve&time=8"
                        autoPlay={shouldPlayVideo}
                        videoRef={videoRef}
                        sx={{
                            backgroundColor: 'var(--landing-surface-muted, #F6F5F4)',
                            border: 'none',
                            borderRadius: 'inherit',
                        }}
                    />
                </Box>
            </Box>

            {/* Mobile video (3:4 aspect ratio) */}
            <Box
                sx={{
                    display: { xs: 'block', md: 'none' },
                    width: '100%',
                    maxWidth: '343px',
                    mx: 'auto',
                }}
            >
                <Box
                    sx={{
                        borderRadius: '14px',
                        border: '1px solid var(--landing-border, rgba(0,0,0,0.12))',
                        overflow: 'hidden',
                        backgroundColor: 'var(--landing-surface, white)',
                        boxShadow: 'var(--landing-media-shadow, inset 0 0 0 1px rgba(0,0,0,0.04), 0px 12px 30px rgba(0,0,0,0.08))',
                    }}
                >
                    <VideoPlaceholder
                        aspectRatio="3/4"
                        playbackId="s9UFAatOuze1IM66tXLP7HeFTuz81NorTswhWrXY7Y00"
                        posterSrc="https://image.mux.com/s9UFAatOuze1IM66tXLP7HeFTuz81NorTswhWrXY7Y00/thumbnail.png?width=750&height=1000&fit_mode=preserve&time=2"
                        autoPlay={shouldPlayVideo}
                        sx={{
                            backgroundColor: 'var(--landing-surface-muted, #F6F5F4)',
                            border: 'none',
                            borderRadius: 'inherit',
                        }}
                    />
                </Box>
            </Box>

            {/* CTA below video */}
            <Box
                sx={{
                    opacity: showCta ? 1 : 0,
                    transform: showCta ? 'translateY(0)' : 'translateY(6px)',
                    transition: 'opacity 500ms ease, transform 500ms ease',
                    mt: { xs: 1.5, md: 2 },
                }}
            >
                <Button
                    variant="contained"
                    disableElevation
                    onClick={(e) => {
                        handleSignupFormOpen(e);
                        trackLandingEvent("landing_cta_click_section3_get_started");
                    }}
                    sx={{
                        fontFamily: '"Space Mono", monospace',
                        fontWeight: 700,
                        fontSize: '14px',
                        letterSpacing: '-0.5px',
                        textTransform: 'uppercase',
                        color: 'var(--landing-action-text, white)',
                        backgroundColor: 'var(--landing-action-bg, black)',
                        borderRadius: '999px',
                        px: 4,
                        py: 1.2,
                        '&:hover': { backgroundColor: 'var(--landing-action-hover, #222)' },
                    }}
                >
                    Get Started
                </Button>
            </Box>
        </Box>
    );
};



// Small square tab icons derived from the floating hero icons
const TabIconOrangePlayback = () => (
    <svg width="36" height="36" viewBox="0 0 28 28" fill="none">
        <rect width="28" height="28" rx="7" fill="#FBEAD6"/>
        <path d="M10.5 10.5H17.5" stroke="#ED6F39" strokeWidth="1.2" strokeLinecap="round"/>
        <path d="M11.5 8.5H16.5" stroke="#ED6F39" strokeWidth="1.2" strokeLinecap="round"/>
        <rect x="9" y="12.5" width="10" height="7" rx="1.5" stroke="#ED6F39" strokeWidth="1.2"/>
        <path d="M13 14.5V18L15.5 16.25L13 14.5Z" fill="#ED6F39"/>
    </svg>
);

const TabIconRedTimeline = () => (
    <svg width="36" height="36" viewBox="0 0 28 28" fill="none">
        <rect width="28" height="28" rx="7" fill="#FCECE4"/>
        <rect x="7" y="9" width="14" height="10" rx="2" stroke="#EB4241" strokeWidth="1.2"/>
        <path d="M12 9V19" stroke="#EB4241" strokeWidth="1.2"/>
        <path d="M16 9V19" stroke="#EB4241" strokeWidth="1.2"/>
        <path d="M7 14H21" stroke="#EB4241" strokeWidth="1.2"/>
    </svg>
);

const TabIconBlueAudio = () => (
    <svg width="36" height="36" viewBox="0 0 28 28" fill="none">
        <rect width="28" height="28" rx="7" fill="#E5F8FE"/>
        <path d="M14 8V20" stroke="#4A9EE4" strokeWidth="1.3" strokeLinecap="round"/>
        <path d="M10 11V17" stroke="#4A9EE4" strokeWidth="1.3" strokeLinecap="round"/>
        <path d="M18 11V17" stroke="#4A9EE4" strokeWidth="1.3" strokeLinecap="round"/>
        <path d="M8 13V15" stroke="#4A9EE4" strokeWidth="1.3" strokeLinecap="round"/>
        <path d="M20 13V15" stroke="#4A9EE4" strokeWidth="1.3" strokeLinecap="round"/>
    </svg>
);

const TabIconPurpleMultitrack = () => (
    <svg width="36" height="36" viewBox="0 0 28 28" fill="none">
        <rect width="28" height="28" rx="7" fill="#F4E6FD"/>
        <circle cx="11" cy="11" r="3" stroke="#9A64F6" strokeWidth="1.2"/>
        <circle cx="17" cy="17" r="3" stroke="#9A64F6" strokeWidth="1.2"/>
        <path d="M13.5 9.5L19.5 15.5" stroke="#9A64F6" strokeWidth="1.2" strokeLinecap="round"/>
    </svg>
);

const TabIconGreenClips = () => (
    <svg width="36" height="36" viewBox="0 0 28 28" fill="none">
        <rect width="28" height="28" rx="7" fill="#E4F7E8"/>
        <rect x="8" y="9" width="7" height="10" rx="1.5" stroke="#43A854" strokeWidth="1.2"/>
        <rect x="17" y="11" width="4" height="6" rx="1" stroke="#43A854" strokeWidth="1.2"/>
        <path d="M11.5 12V16" stroke="#43A854" strokeWidth="1.2" strokeLinecap="round"/>
    </svg>
);

// Upload icon with cloud and up arrow
const UploadIcon = () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 16V8m0 0l-3 3m3-3l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M17 16.5c1.933 0 3.5-1.567 3.5-3.5 0-1.698-1.212-3.112-2.82-3.43A5.002 5.002 0 0 0 8 8.5c0 .18.01.357.028.531A3.5 3.5 0 0 0 4.5 12.5c0 1.933 1.567 3.5 3.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

// Microphone icon
const RecordIcon = () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="9" y="3" width="6" height="11" rx="3" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M5 12a7 7 0 0 0 14 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M12 19v2m-3 0h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

const FEATURE_SHOWCASE_ITEMS = [
    {
        icon: <TabIconGreenClips />,
        title: 'A recording studio for you and your guests',
        description: 'Record on your own or invite guests to a remote session. Capture audio and video locally on each participant’s device, with video quality up to 4K.\n\nAlready recorded elsewhere? Upload your recording and get straight to the edit.',
        mobileDescription: 'Record solo or with guests in up to 4K, with local capture for every participant. Already recorded elsewhere? Upload it and get straight to the edit.',
        resultLine: 'Guest session → recorded tracks → editor',
        playbackId: 'sPO02GlkMGTAYRxxPGM0002Kh3hwBrEnZhMClsjEiOwRcU',
        desktopPosterId: 'sPO02GlkMGTAYRxxPGM0002Kh3hwBrEnZhMClsjEiOwRcU',
        mobilePlaybackId: '7MM6VDC00qFAdIBIcAWGkQNv9EPzKlfxCIvPkJ5RRHNk',
        videoSrc: 'https://storage.googleapis.com/libretto-public-assets/videos/RecordShow.mp4',
    },
    {
        icon: <TabIconRedTimeline />,
        title: 'Edit your episode by editing text',
        description: 'Remove a passage from the transcript and the matching audio or video is cut from the timeline. Review the resulting edit without searching through your recording.',
        mobileDescription: 'Remove a passage from the transcript and the matching audio or video is cut from the timeline. Review the result right away.',
        resultLine: 'Make a first cut by editing words',
        playbackId: 'WPWe02R9iv5u1R0100vsFgN15nQjgtieq2jnA6U00t02npU00',
        desktopPosterId: '8rlWCGQ6I102QX17TqRvQip7uPTZcOz7A00uducpBAOQA',
        mobilePlaybackId: 'N9SndVKZExb029uy9VWWG5H6zaY00a4XD9ZQ3roiWwWUM',
        videoSrc: 'https://storage.googleapis.com/libretto-public-assets/videos/EditByTranscriptDark.mp4',
    },
    {
        icon: <TabIconOrangePlayback />,
        title: 'Remove filler words and tighten your delivery',
        description: 'Find filler words, long pauses, and retakes in one pass. Review every suggested cleanup and adjust the result before committing.',
        mobileDescription: 'Find filler words, long pauses, and retakes in one pass. Review and adjust every suggested cleanup.',
        resultLine: 'Keep the delivery natural while tightening the pace',
        playbackId: 'K1Lz63vWXhweFlrjk5BS0001LmOfTWRDyrIHkTqatfSos',
        desktopPosterId: 'jcDQeMz4xXiii5BASuhni5XmfT24IxbyYK8rScKc0102I',
        mobilePlaybackId: 'DrtDj02AodTCZ02QAcq4ugUqIYvSt9PF7S8mL7PPjCglE',
        videoSrc: 'https://storage.googleapis.com/libretto-public-assets/videos/ClarityDark.mp4',
    },
    {
        icon: <TabIconPurpleMultitrack />,
        title: 'Make the small edits that finish an episode',
        description: 'Cut a passage across the conversation, silence an unwanted sound on one speaker’s track, and fade your intro or outro in and out. Fine-tune the details while keeping your audio and video aligned.',
        mobileDescription: 'Cut across the conversation, silence one speaker’s track, and fade your intro or outro while keeping every track aligned.',
        resultLine: 'Fine control without losing sync',
        playbackId: 'dQb00ZgNg2hLg5VUhJC8YvjUx02Lx1fIAaG7T5ZZFQJYs',
        desktopPosterId: 'bdq01A00001Oy4xgM6SWq7LhhUjIC02gpyOHqShbY1JT8sI',
        mobilePlaybackId: 'E5ZHw2WY6pFl8oJTOlbsf5mcvsWnE5t1iLG02QDeyQks',
        videoSrc: 'https://storage.googleapis.com/libretto-public-assets/videos/EditMultipleDark.mp4',
    },
    {
        icon: <TabIconBlueAudio />,
        title: 'Clean up your sound',
        description: 'Remove background noise, balance levels, and compare the before-and-after result before you export your episode.',
        mobileDescription: 'Remove background noise, balance levels, and hear the before-and-after result before export.',
        resultLine: 'Hear the difference before you publish',
        playbackId: 'AlKKoUv5plz3zVhGNousaBbju1OqZj4500CNSBamJppE',
        desktopPosterId: 'T87v01j500vH01IAwNaNhmxVRmQGa018E4pmvtX5iou4eks',
        mobilePlaybackId: '8sjniCmR00HVcQJT1HXDPoeaiG9MgQhbOIi5iLr6kBpM',
        videoSrc: 'https://storage.googleapis.com/libretto-public-assets/videos/StudioSoundDark.mp4',
    },
    // Hidden until video loop is created
    // {
    //     icon: <TabIconGreenClips />,
    //     title: 'Turn your episode into shareable clips',
    //     description: 'Automatically find highlights and turn them into short clips with captions. No re-editing. No extra tools.',
    //     mobileDescription: 'Find highlights and turn them into short clips with captions. No re-editing. No extra tools.',
    //     resultLine: 'Ready-to-share clips instantly',
    //     playbackId: '',
    //     desktopPosterId: '',
    //     mobilePlaybackId: '',
    //     videoSrc: '',
    // },
];

const FeatureShowcaseRow = React.forwardRef<HTMLDivElement, {item: typeof FEATURE_SHOWCASE_ITEMS[0]; shouldPlayVideo: boolean; isMobile: boolean; index: number; isActiveRow: boolean}>(({item, shouldPlayVideo, isMobile, index, isActiveRow}, forwardedRef) => {
    const videoRef = React.useRef<HTMLVideoElement | null>(null);

    // Play/pause: only the active (most centered) row plays
    React.useEffect(() => {
        const video = videoRef.current;
        if (!video) return;
        if (isActiveRow && shouldPlayVideo) {
            video.play().catch(() => {});
        } else {
            video.pause();
        }
    }, [isActiveRow, shouldPlayVideo]);

    const textContent = (
        <Box
            sx={{
                flex: {xs: '1 1 100%', md: '1 1 0'},
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: '14px',
            }}
        >
            {/* Icon */}
            <Box
                sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '14px',
                    backgroundColor: 'var(--landing-border-subtle, rgba(0,0,0,0.04))',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--landing-text-primary, black)',
                }}
            >
                {item.icon}
            </Box>

            {/* Title */}
            <Typography
                sx={{
                    fontFamily: '"Inter", sans-serif',
                    fontWeight: 600,
                    fontSize: {xs: '22px', md: '26px'},
                    lineHeight: 1.25,
                    letterSpacing: '-0.5px',
                    color: 'var(--landing-text-primary, black)',
                }}
            >
                {item.title}
            </Typography>

            {/* Description */}
            <Typography
                sx={{
                    fontFamily: '"Inter", sans-serif',
                    fontWeight: 400,
                    fontSize: {xs: '15px', md: '17px'},
                    lineHeight: 1.65,
                    color: 'var(--landing-text-55, rgba(0,0,0,0.55))',
                    whiteSpace: 'pre-line',
                }}
            >
                {isMobile && item.mobileDescription ? item.mobileDescription : item.description}
            </Typography>

            {/* Result line */}
            {item.resultLine && (
                <Typography
                    sx={{
                        fontFamily: '"Inter", sans-serif',
                        fontWeight: 500,
                        fontStyle: 'italic',
                        fontSize: {xs: '14px', md: '15px'},
                        lineHeight: 1.5,
                        color: 'var(--landing-text-35, rgba(0,0,0,0.35))',
                        mt: '4px',
                    }}
                >
                    {item.resultLine}
                </Typography>
            )}
        </Box>
    );

    const mediaContent = (
        <Box
            sx={{
                flex: {xs: '1 1 100%', md: '0 0 740px'},
                maxWidth: '100%',
            }}
        >
            <Box
                sx={{
                    borderRadius: {xs: '14px', md: '16px'},
                    border: '1px solid var(--landing-border, rgba(0,0,0,0.1))',
                    overflow: 'hidden',
                    backgroundColor: 'var(--landing-surface-muted, #F6F5F4)',
                    boxShadow: 'var(--landing-card-shadow, 0px 8px 24px rgba(0,0,0,0.06))',
                }}
            >
                <VideoPlaceholder
                    aspectRatio={isMobile ? '3/4' : '740/420'}
                    {...(isMobile
                        ? {playbackId: item.mobilePlaybackId}
                        : {videoSrc: item.videoSrc}
                    )}
                    posterSrc={`https://image.mux.com/${isMobile ? item.mobilePlaybackId : (item.desktopPosterId || item.playbackId)}/thumbnail.png?time=0.5&width=${isMobile ? 900 : 1280}&height=${isMobile ? 1200 : 720}&fit_mode=preserve`}
                    autoPlay={false}
                    loop={true}
                    videoRef={videoRef}
                    sx={{
                        backgroundColor: 'var(--landing-surface-muted, #F6F5F4)',
                        border: 'none',
                        borderRadius: 'inherit',
                    }}
                />
            </Box>
        </Box>
    );

    return (
        <Box
            ref={forwardedRef}
            sx={{
                display: 'flex',
                flexDirection: {xs: 'column', md: 'row'},
                alignItems: {xs: 'flex-start', md: 'center'},
                gap: {xs: '28px', md: '64px'},
                width: '100%',
                maxWidth: '1200px',
                mx: 'auto',
            }}
        >
            {textContent}
            {mediaContent}
        </Box>
    );
});

const FeatureShowcaseSection = ({shouldPlayVideo, isMobile}: {shouldPlayVideo: boolean; isMobile: boolean}) => {
    const [activeRowIndex, setActiveRowIndex] = React.useState(-1);
    const rowRefs = React.useRef<(HTMLDivElement | null)[]>([]);

    // Track which row is closest to viewport center; only that one plays
    React.useEffect(() => {
        const handleScroll = () => {
            const viewportCenter = window.innerHeight / 2;
            let closestIndex = -1;
            let closestDistance = Infinity;

            rowRefs.current.forEach((el, i) => {
                if (!el) return;
                const rect = el.getBoundingClientRect();
                const rowCenter = rect.top + rect.height / 2;
                const distance = Math.abs(rowCenter - viewportCenter);
                // Only consider rows that are at least partially visible
                if (rect.bottom > 0 && rect.top < window.innerHeight && distance < closestDistance) {
                    closestDistance = distance;
                    closestIndex = i;
                }
            });

            setActiveRowIndex(closestIndex);
        };

        window.addEventListener('scroll', handleScroll, {passive: true});
        handleScroll(); // initial check
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: '100%',
                gap: {xs: '48px', md: '72px'},
            }}
        >
            {/* Section header */}
            <Box sx={{ textAlign: 'center', mb: { xs: '12px', md: '16px' } }}>
                <Typography
                    sx={{
                        fontFamily: 'Arial, sans-serif',
                        fontWeight: 700,
                        fontSize: {xs: '32px', md: '48px'},
                        lineHeight: 1.15,
                        letterSpacing: '-1.5px',
                        color: 'var(--landing-text-primary, black)',
                        mb: '8px',
                    }}
                >
                    From recording to finished episode
                </Typography>
                <Typography
                    sx={{
                        fontFamily: '"Inter", sans-serif',
                        fontWeight: 400,
                        fontSize: {xs: '16px', md: '18px'},
                        lineHeight: 1.5,
                        color: 'var(--landing-text-50, rgba(0,0,0,0.5))',
                    }}
                >
                    Record, edit, clean up, and finish your episode in one workspace
                </Typography>
            </Box>

            {/* Feature rows */}
            {FEATURE_SHOWCASE_ITEMS.map((item, index) => (
                <FeatureShowcaseRow
                    key={item.title}
                    item={item}
                    shouldPlayVideo={shouldPlayVideo}
                    isMobile={isMobile}
                    index={index}
                    isActiveRow={index === activeRowIndex}
                    ref={(el: HTMLDivElement | null) => { rowRefs.current[index] = el; }}
                />
            ))}
        </Box>
    );
};

const CollaborationCallout = () => (
    <Box
        sx={{
            width: '100%',
            maxWidth: '1200px',
            mx: 'auto',
            display: 'flex',
            flexDirection: {xs: 'column', md: 'row'},
            alignItems: {xs: 'stretch', md: 'center'},
            justifyContent: 'space-between',
            gap: {xs: '28px', md: '56px'},
            p: {xs: '24px', md: '36px 40px'},
            borderRadius: {xs: '24px', md: '32px'},
            backgroundColor: 'var(--landing-surface-muted, #F6F5F4)',
            border: '1px solid var(--landing-border-subtle, rgba(0,0,0,0.04))',
        }}
    >
        <Box sx={{maxWidth: '590px'}}>
            <Typography
                sx={{
                    fontFamily: '"Space Mono", monospace',
                    fontSize: '12px',
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: 'var(--landing-text-45, rgba(0,0,0,0.45))',
                    mb: '10px',
                }}
            >
                Collaboration
            </Typography>
            <Typography
                component="h2"
                sx={{
                    fontFamily: 'Arial, sans-serif',
                    fontWeight: 700,
                    fontSize: {xs: '26px', md: '34px'},
                    lineHeight: 1.2,
                    letterSpacing: '-1px',
                    color: 'var(--landing-text-primary, black)',
                    mb: '12px',
                }}
            >
                Bring your editor into the workspace
            </Typography>
            <Typography
                sx={{
                    fontFamily: '"Inter", sans-serif',
                    fontSize: {xs: '15px', md: '17px'},
                    lineHeight: 1.65,
                    color: 'var(--landing-text-55, rgba(0,0,0,0.55))',
                }}
            >
                Share access to your projects so you and your collaborators can work on the same episode in Libretto.
            </Typography>
        </Box>

        <Box
            aria-label="Shared episode workspace preview"
            sx={{
                width: {xs: '100%', md: '380px'},
                flex: {md: '0 0 380px'},
                borderRadius: '18px',
                backgroundColor: 'var(--landing-surface, white)',
                border: '1px solid var(--landing-border, rgba(0,0,0,0.1))',
                boxShadow: 'var(--landing-card-shadow, 0px 8px 24px rgba(0,0,0,0.06))',
                p: {xs: '18px', md: '20px'},
            }}
        >
            <Typography
                sx={{
                    fontFamily: '"Inter", sans-serif',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: 'var(--landing-text-45, rgba(0,0,0,0.45))',
                    mb: '8px',
                }}
            >
                SHARED PROJECT
            </Typography>
            <Typography
                sx={{
                    fontFamily: '"Inter", sans-serif',
                    fontSize: {xs: '17px', md: '18px'},
                    fontWeight: 600,
                    color: 'var(--landing-text-primary, black)',
                    mb: '18px',
                }}
            >
                The conversation: Episode 12
            </Typography>
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px'}}>
                <Box sx={{display: 'flex', alignItems: 'center'}}>
                    {[
                        {label: 'N', background: '#F5B96A'},
                        {label: 'M', background: '#74C7B8'},
                        {label: 'E', background: '#8DB6E8'},
                    ].map((member, index) => (
                        <Box
                            key={member.label}
                            sx={{
                                width: 34,
                                height: 34,
                                ml: index === 0 ? 0 : '-8px',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: member.background,
                                border: '2px solid var(--landing-surface, white)',
                                color: '#2C241B',
                                fontFamily: '"Inter", sans-serif',
                                fontSize: '12px',
                                fontWeight: 700,
                            }}
                        >
                            {member.label}
                        </Box>
                    ))}
                </Box>
                <Box sx={{display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end'}}>
                    {['Editor', 'Viewer'].map((role) => (
                        <Box
                            key={role}
                            sx={{
                                px: '10px',
                                py: '5px',
                                borderRadius: '999px',
                                backgroundColor: 'var(--landing-surface-muted, #F6F5F4)',
                                color: 'var(--landing-text-60, rgba(0,0,0,0.6))',
                                fontFamily: '"Inter", sans-serif',
                                fontSize: '12px',
                                fontWeight: 600,
                            }}
                        >
                            {role}
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    </Box>
);

// Use cases icons
const PodcastIcon = () => (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
        <path d="M12 1a4 4 0 0 0-4 4v6a4 4 0 0 0 8 0V5a4 4 0 0 0-4-4Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M19 10v1a7 7 0 0 1-14 0v-1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M12 18v4m-3 0h6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

const VideoIcon = () => (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
        <rect x="2" y="4" width="15" height="16" rx="2" stroke="currentColor" strokeWidth="1.4"/>
        <path d="M17 9l5-3v12l-5-3V9Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

const InterviewIcon = () => (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
        <circle cx="9" cy="7" r="3" stroke="currentColor" strokeWidth="1.4"/>
        <circle cx="17" cy="7" r="3" stroke="currentColor" strokeWidth="1.4"/>
        <path d="M2 21v-2a5 5 0 0 1 5-5h4a5 5 0 0 1 5 5v2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
        <path d="M17 14a5 5 0 0 1 5 5v2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
    </svg>
);

const USE_CASE_ITEMS = [
    {
        icon: <PodcastIcon />,
        title: 'Podcasts',
        description: 'Record or upload your episode and clean it up in one pass. Remove filler words, tighten pauses, edit by transcript, and export a polished episode ready to publish.',
        tagline: 'From raw recording to finished episode.',
    },
    {
        icon: <VideoIcon />,
        title: 'Talking-head videos',
        description: 'Record or upload your video and cut it by editing the transcript. Remove retakes, clean up your delivery, and shape the message before export.',
        tagline: 'Go from camera to finished cut in one workspace.',
    },
    {
        icon: <InterviewIcon />,
        title: 'Interviews & conversations',
        description: 'Invite guests to a remote recording or upload separate tracks. Clean up pacing, remove filler speech, and make precise edits while keeping everyone aligned.',
        tagline: 'Record, shape, and finish the conversation together.',
    },
];

const UseCasesSection = ({isMobile}: {isMobile: boolean}) => {
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: '100%',
                maxWidth: '1200px',
                mx: 'auto',
            }}
        >
            {/* Header */}
            <Typography
                sx={{
                    fontFamily: '"Arial", sans-serif',
                    fontWeight: 700,
                    fontSize: {xs: '28px', md: '40px'},
                    lineHeight: 1.2,
                    letterSpacing: '-1px',
                    color: 'var(--landing-text-primary, black)',
                    textAlign: 'center',
                    mb: '12px',
                }}
            >
                Built for long-form spoken content
            </Typography>
            <Typography
                sx={{
                    fontFamily: '"Inter", sans-serif',
                    fontWeight: 400,
                    fontSize: {xs: '16px', md: '18px'},
                    lineHeight: 1.5,
                    color: 'var(--landing-text-50, rgba(0,0,0,0.5))',
                    textAlign: 'center',
                    mb: {xs: '32px', md: '48px'},
                }}
            >
                Podcasts, interviews, and talking-head videos
            </Typography>

            {/* Three columns */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: {xs: 'column', md: 'row'},
                    gap: {xs: '48px', md: '64px'},
                    width: '100%',
                }}
            >
                {USE_CASE_ITEMS.map((item) => (
                    <Box
                        key={item.title}
                        sx={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '16px',
                        }}
                    >
                        {/* Icon */}
                        <Box sx={{color: 'var(--landing-text-70, rgba(0,0,0,0.7))', mb: '4px'}}>
                            {item.icon}
                        </Box>

                        {/* Title */}
                        <Typography
                            sx={{
                                fontFamily: '"Inter", sans-serif',
                                fontWeight: 600,
                                fontSize: {xs: '20px', md: '22px'},
                                lineHeight: 1.3,
                                letterSpacing: '-0.3px',
                                color: 'var(--landing-text-primary, black)',
                            }}
                        >
                            {item.title}
                        </Typography>

                        {/* Description */}
                        <Typography
                            sx={{
                                fontFamily: '"Inter", sans-serif',
                                fontWeight: 400,
                                fontSize: {xs: '15px', md: '16px'},
                                lineHeight: 1.65,
                                color: 'var(--landing-text-55, rgba(0,0,0,0.55))',
                            }}
                        >
                            {item.description}
                        </Typography>

                        {/* Tagline */}
                        <Typography
                            sx={{
                                fontFamily: '"Inter", sans-serif',
                                fontWeight: 400,
                                fontStyle: 'italic',
                                fontSize: {xs: '14px', md: '15px'},
                                lineHeight: 1.5,
                                color: 'var(--landing-text-40, rgba(0,0,0,0.4))',
                                mt: 'auto',
                                pt: '16px',
                            }}
                        >
                            {item.tagline}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

const CONTENT_TYPE_OPTIONS = [
    'Podcast',
    'YouTube / talking-head videos',
    'Other',
];

const RECORDING_SETUP_OPTIONS = [
    'Zoom',
    'Local recording',
    'Other',
];

const demoFormLabelSx = {
    fontFamily: '"Inter", sans-serif',
    fontWeight: 600,
    fontSize: '13px',
    lineHeight: '20px',
    color: 'var(--landing-text-primary, black)',
    mb: 0.5,
} as const;

const demoFormRadioLabelSx = {
    '& .MuiFormControlLabel-label': {
        fontFamily: '"Inter", sans-serif',
        fontSize: '14px',
        lineHeight: '22px',
        letterSpacing: '-0.2px',
        color: 'var(--landing-text-primary, black)',
    },
} as const;

const submitDemoRequest = (data: {
    name: string;
    email: string;
    contentType?: string;
    recordingSetup?: string;
    helpWith?: string;
}) => {
    try {
        const baseUrl = String(BASE_URL).replace(/\/+$/, "");
        fetch(`${baseUrl}/landing/demo-request`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(data),
            keepalive: true,
        }).catch(() => {});
    } catch {
        // Never break UI flows.
    }
};

const DemoRequestDialog = ({
    open,
    onClose,
    handleSignupFormOpen,
}: {
    open: boolean;
    onClose: () => void;
    handleSignupFormOpen: (e?: any) => void;
}) => {
    const [submitted, setSubmitted] = useState(false);

    // Form state
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [contentType, setContentType] = useState('');
    const [contentTypeOther, setContentTypeOther] = useState('');
    const [recordingSetup, setRecordingSetup] = useState('');
    const [recordingSetupOther, setRecordingSetupOther] = useState('');
    const [helpWith, setHelpWith] = useState('');

    // Validation
    const [nameError, setNameError] = useState(false);
    const [emailError, setEmailError] = useState(false);

    const resetForm = () => {
        setSubmitted(false);
        setName('');
        setEmail('');
        setContentType('');
        setContentTypeOther('');
        setRecordingSetup('');
        setRecordingSetupOther('');
        setHelpWith('');
        setNameError(false);
        setEmailError(false);
    };

    const handleClose = () => {
        onClose();
        // Reset after close animation finishes
        setTimeout(resetForm, 200);
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        const trimmedName = name.trim();
        const trimmedEmail = email.trim();
        const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);

        let hasError = false;
        if (!trimmedName) { setNameError(true); hasError = true; } else { setNameError(false); }
        if (!trimmedEmail || !isEmailValid) { setEmailError(true); hasError = true; } else { setEmailError(false); }
        if (hasError) return;

        const resolvedContentType = contentType === 'Other' ? (contentTypeOther.trim() || 'Other') : contentType;
        const resolvedSetup = recordingSetup === 'Other' ? (recordingSetupOther.trim() || 'Other') : recordingSetup;

        submitDemoRequest({
            name: trimmedName,
            email: trimmedEmail,
            contentType: resolvedContentType || undefined,
            recordingSetup: resolvedSetup || undefined,
            helpWith: helpWith.trim() || undefined,
        });
        trackLandingEvent("landing_demo_request_submitted");
        setSubmitted(true);
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            aria-labelledby="demo-request-dialog"
            sx={{
                "& .MuiDialog-paper": {
                    borderRadius: "20px",
                    maxWidth: '480px',
                    width: '100%',
                    mx: 2,
                },
            }}
        >
            <DialogContent
                sx={{
                    px: { xs: 3, sm: 4 },
                    py: { xs: 3, sm: 4 },
                }}
            >
                {submitted ? (
                    /* ── Confirmation view ── */
                    <Box sx={{ textAlign: 'center', py: 2 }}>
                        <Typography
                            sx={{
                                fontFamily: '"Hedvig Letters Serif", serif',
                                fontSize: { xs: '22px', sm: '26px' },
                                lineHeight: { xs: '28px', sm: '32px' },
                                letterSpacing: '-1px',
                                color: 'var(--landing-text-primary, black)',
                                mb: 1.5,
                            }}
                        >
                            Thanks! We&apos;ll reach out shortly.
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: '"Inter", sans-serif',
                                fontSize: '15px',
                                lineHeight: '22px',
                                letterSpacing: '-0.2px',
                                color: 'var(--landing-text-primary, black)',
                                opacity: 0.7,
                                mb: 3,
                            }}
                        >
                            We&apos;ll be in touch to schedule the demo. In the meantime, you can create your Libretto account.
                        </Typography>
                        <Button
                            variant="contained"
                            fullWidth
                            onClick={(e) => {
                                handleClose();
                                handleSignupFormOpen(e);
                            }}
                            sx={{
                                backgroundColor: 'var(--landing-action-bg, #000)',
                                color: 'var(--landing-action-text, #fff)',
                                height: 44,
                                borderRadius: '12px',
                                textTransform: 'none',
                                fontFamily: '"Inter", sans-serif',
                                fontWeight: 500,
                                fontSize: '15px',
                                boxShadow: '0px 1px 4px rgba(0,0,0,0.25)',
                                '&:hover': {
                                    backgroundColor: 'var(--landing-action-hover, #111)',
                                },
                            }}
                        >
                            Create a free account
                        </Button>
                    </Box>
                ) : (
                    /* ── Form view ── */
                    <Box>
                        <Typography
                            sx={{
                                fontFamily: '"Hedvig Letters Serif", serif',
                                fontSize: { xs: '22px', sm: '26px' },
                                lineHeight: { xs: '28px', sm: '32px' },
                                letterSpacing: '-1px',
                                color: 'var(--landing-text-primary, black)',
                                mb: 0.5,
                            }}
                        >
                            Book a demo
                        </Typography>
                        {/*<Typography*/}
                        {/*    sx={{*/}
                        {/*        fontFamily: '"Inter", sans-serif',*/}
                        {/*        fontSize: '14px',*/}
                        {/*        lineHeight: '20px',*/}
                        {/*        letterSpacing: '-0.2px',*/}
                        {/*        color: 'var(--landing-text-primary, black)',*/}
                        {/*        opacity: 0.6,*/}
                        {/*        mb: 3,*/}
                        {/*    }}*/}
                        {/*>*/}
                        {/*    We&apos;ll walk through your workflow and show how Libretto fits.*/}
                        {/*</Typography>*/}

                        <form onSubmit={handleSubmit}>
                            {/* Name */}
                            <Typography sx={demoFormLabelSx}>Name *</Typography>
                            <TextField
                                variant="outlined"
                                fullWidth
                                size="small"
                                value={name}
                                onChange={(e) => { setName(e.target.value); if (nameError) setNameError(false); }}
                                error={nameError}
                                helperText={nameError ? 'Name is required' : ''}
                                sx={{
                                    mb: 2,
                                    '& .MuiInputBase-root': {
                                        height: 44,
                                        fontFamily: '"Inter", sans-serif',
                                        fontSize: '14px',
                                        borderRadius: '10px',
                                    },
                                }}
                            />

                            {/* Email */}
                            <Typography sx={demoFormLabelSx}>Email *</Typography>
                            <TextField
                                variant="outlined"
                                fullWidth
                                size="small"
                                type="email"
                                value={email}
                                onChange={(e) => { setEmail(e.target.value); if (emailError) setEmailError(false); }}
                                error={emailError}
                                helperText={emailError ? 'Please enter a valid email' : ''}
                                sx={{
                                    mb: 2.5,
                                    '& .MuiInputBase-root': {
                                        height: 44,
                                        fontFamily: '"Inter", sans-serif',
                                        fontSize: '14px',
                                        borderRadius: '10px',
                                    },
                                }}
                            />

                            {/* Content type (optional) */}
                            <Typography sx={{ ...demoFormLabelSx, mb: 0.5 }}>
                                What type of content do you create?
                                <Box component="span" sx={{ fontWeight: 400, opacity: 0.5, ml: 0.5 }}>(optional)</Box>
                            </Typography>
                            <RadioGroup
                                value={contentType}
                                onChange={(e) => setContentType(e.target.value)}
                                sx={{ mb: contentType === 'Other' ? 1 : 2.5, pl: 0.5 }}
                            >
                                {CONTENT_TYPE_OPTIONS.map((option) => (
                                    <FormControlLabel
                                        key={option}
                                        value={option}
                                        control={<Radio size="small" sx={{ color: 'var(--landing-text-30, rgba(0,0,0,0.3))', '&.Mui-checked': { color: 'var(--landing-text-primary, black)' }, py: 0.5 }} />}
                                        label={option}
                                        sx={demoFormRadioLabelSx}
                                    />
                                ))}
                            </RadioGroup>
                            {contentType === 'Other' && (
                                <TextField
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                    placeholder="Please specify"
                                    value={contentTypeOther}
                                    onChange={(e) => setContentTypeOther(e.target.value)}
                                    sx={{
                                        mb: 2.5,
                                        ml: 0.5,
                                        '& .MuiInputBase-root': {
                                            height: 40,
                                            fontFamily: '"Inter", sans-serif',
                                            fontSize: '14px',
                                            borderRadius: '10px',
                                        },
                                    }}
                                />
                            )}

                            {/* Recording setup (optional) */}
                            <Typography sx={{ ...demoFormLabelSx, mb: 0.5 }}>
                                Recording setup
                                <Box component="span" sx={{ fontWeight: 400, opacity: 0.5, ml: 0.5 }}>(optional)</Box>
                            </Typography>
                            <RadioGroup
                                value={recordingSetup}
                                onChange={(e) => setRecordingSetup(e.target.value)}
                                sx={{ mb: recordingSetup === 'Other' ? 1 : 2.5, pl: 0.5 }}
                            >
                                {RECORDING_SETUP_OPTIONS.map((option) => (
                                    <FormControlLabel
                                        key={option}
                                        value={option}
                                        control={<Radio size="small" sx={{ color: 'var(--landing-text-30, rgba(0,0,0,0.3))', '&.Mui-checked': { color: 'var(--landing-text-primary, black)' }, py: 0.5 }} />}
                                        label={option}
                                        sx={demoFormRadioLabelSx}
                                    />
                                ))}
                            </RadioGroup>
                            {recordingSetup === 'Other' && (
                                <TextField
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                    placeholder="Please specify"
                                    value={recordingSetupOther}
                                    onChange={(e) => setRecordingSetupOther(e.target.value)}
                                    sx={{
                                        mb: 2.5,
                                        ml: 0.5,
                                        '& .MuiInputBase-root': {
                                            height: 40,
                                            fontFamily: '"Inter", sans-serif',
                                            fontSize: '14px',
                                            borderRadius: '10px',
                                        },
                                    }}
                                />
                            )}

                            {/* Help with (optional) */}
                            <Typography sx={{ ...demoFormLabelSx, mb: 0.5 }}>
                                Anything else you&apos;d like us to know or cover in the demo?
                                <Box component="span" sx={{ fontWeight: 400, opacity: 0.5, ml: 0.5 }}>(optional)</Box>
                            </Typography>
                            <TextField
                                variant="outlined"
                                fullWidth
                                multiline
                                minRows={2}
                                maxRows={4}
                                value={helpWith}
                                onChange={(e) => setHelpWith(e.target.value)}
                                sx={{
                                    mb: 3,
                                    '& .MuiInputBase-root': {
                                        fontFamily: '"Inter", sans-serif',
                                        fontSize: '14px',
                                        borderRadius: '10px',
                                    },
                                }}
                            />

                            <Button
                                variant="contained"
                                type="submit"
                                fullWidth
                                sx={{
                                    backgroundColor: 'var(--landing-action-bg, #000)',
                                    color: 'var(--landing-action-text, #fff)',
                                    height: 44,
                                    borderRadius: '12px',
                                    textTransform: 'none',
                                    fontFamily: '"Inter", sans-serif',
                                    fontWeight: 500,
                                    fontSize: '15px',
                                    boxShadow: '0px 1px 4px rgba(0,0,0,0.25)',
                                    '&:hover': {
                                        backgroundColor: 'var(--landing-action-hover, #111)',
                                    },
                                }}
                            >
                                Submit
                            </Button>
                        </form>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

const PricingCheckIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M12 23C5.92433 23 1 18.0757 1 12C1 5.92433 5.92433 1 12 1C18.0757 1 23 5.92433 23 12C23 18.0757 18.0757 23 12 23Z"
            fill="#57BE84"
        />
        <path d="M16 10L11 15L8 12" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const PricingFeatureLine = ({text}: {text: string}) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
        <Box sx={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>
            <PricingCheckIcon />
        </Box>
        <Typography
            sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: { xs: '14px', md: '16px' },
                lineHeight: { xs: '20px', md: '24px' },
                letterSpacing: '-0.5px',
                color: 'var(--landing-text-primary, black)',
                opacity: 0.7,
            }}
        >
            {text}
        </Typography>
    </Box>
);

const PricingSection = ({handleSignupFormOpen, onBookDemo}: {handleSignupFormOpen: (opts?: any) => void; onBookDemo: () => void}) => {
    return (
        <Box sx={{ width: '100%' }}>
            <Box sx={{ textAlign: 'center', mb: { xs: '20px', md: '44px' } }}>
                <Typography
                    sx={{
                        fontFamily: '"Hedvig Letters Serif", serif',
                        fontSize: { xs: '32px', md: '52px' },
                        lineHeight: { xs: '34px', md: '67px' },
                        letterSpacing: { xs: '-1.5px', md: '-2.5px' },
                        color: 'var(--landing-text-primary, black)',
                        mb: { xs: 2, md: 2.5 },
                        maxWidth: { xs: '327px', md: 'none' },
                        mx: 'auto',
                    }}
                >
                    Simple pricing. No credit limits.
                </Typography>
            </Box>

            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: { xs: '16px', md: '28px' },
                maxWidth: { xs: '343px', md: '836px' },
                mx: 'auto',
                alignItems: { md: 'stretch' },
            }}>
                {/* Studio card */}
                <Box sx={{ maxWidth: { md: '400px' }, mx: 'auto' }}>
                    <Box
                        sx={{
                            width: '100%',
                            height: '100%',
                            backgroundColor: 'var(--landing-surface-muted, #F6F5F4)',
                            borderRadius: '28px',
                            border: '1px solid var(--landing-border-subtle, rgba(0,0,0,0.03))',
                            overflow: 'hidden',
                            padding: '4px',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        {/* Top (white) */}
                        <Box
                            sx={{
                                backgroundColor: 'var(--landing-surface, white)',
                                borderTopLeftRadius: '24px',
                                borderTopRightRadius: '24px',
                                borderBottomLeftRadius: '8px',
                                borderBottomRightRadius: '8px',
                                paddingTop: '20px',
                                paddingX: '24px',
                                paddingBottom: '18px',
                                textAlign: 'center',
                            }}
                        >
                            <Typography
                                sx={{
                                    fontFamily: '"Space Mono", monospace',
                                    fontWeight: 700,
                                    fontSize: '15px',
                                    lineHeight: '24px',
                                    letterSpacing: '-0.8px',
                                    textTransform: 'uppercase',
                                    color: 'var(--landing-text-primary, black)',
                                }}
                            >
                                Studio
                            </Typography>
                            <Typography
                                sx={{
                                    mt: 1.5,
                                    fontFamily: '"Inter", sans-serif',
                                    fontSize: { xs: '16px', md: '18px' },
                                    lineHeight: { xs: '22px', md: '26px' },
                                    letterSpacing: '-0.5px',
                                    color: 'var(--landing-text-primary, black)',
                                    opacity: 0.7,
                                    px: 1,
                                }}
                            >
                                Edit recordings faster with transcript editing and automatic cleanup.
                            </Typography>

                            <Box sx={{ mt: 3, display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: '6px' }}>
                                <Typography
                                    sx={{
                                        fontFamily: '"Hedvig Letters Serif", serif',
                                        fontSize: '52px',
                                        lineHeight: '52px',
                                        letterSpacing: '-2.5px',
                                        color: 'var(--landing-text-primary, black)',
                                    }}
                                >
                                    $24
                                </Typography>
                                <Typography
                                    sx={{
                                        fontFamily: '"Hedvig Letters Serif", serif',
                                        fontSize: '32px',
                                        lineHeight: '32px',
                                        letterSpacing: '-2.5px',
                                        color: 'var(--landing-text-primary, black)',
                                    }}
                                >
                                    /mo
                                </Typography>
                            </Box>

                            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                                <Button
                                    variant="contained"
                                    onClick={(e) => {
                                        handleSignupFormOpen(e);
                                        trackLandingEvent("landing_cta_click_pricing_get_started");
                                    }}
                                    sx={{
                                        width: '277px',
                                        height: '36px',
                                        backgroundColor: 'var(--landing-action-bg, black)',
                                        color: 'var(--landing-action-text, white)',
                                        borderRadius: '999px',
                                        textTransform: 'uppercase',
                                        fontFamily: '"Space Mono", monospace',
                                        fontWeight: 700,
                                        fontSize: '15px',
                                        lineHeight: '24px',
                                        letterSpacing: '-0.8px',
                                        boxShadow: '0px 1px 4px rgba(0,0,0,0.25)',
                                        transition: 'background-color 200ms ease, transform 200ms ease, box-shadow 200ms ease',
                                        '&:hover': {
                                            backgroundColor: 'var(--landing-action-hover, #111)',
                                            transform: 'translateY(-1px)',
                                            boxShadow: '0px 3px 8px rgba(0,0,0,0.3)',
                                        },
                                    }}
                                >
                                    Get Started
                                </Button>
                            </Box>

                            <Typography
                                sx={{
                                    mt: 1.5,
                                    fontFamily: '"Inter", sans-serif',
                                    fontSize: '13px',
                                    lineHeight: '18px',
                                    letterSpacing: '-0.2px',
                                    color: 'var(--landing-text-primary, black)',
                                    opacity: 0.7,
                                }}
                            >
                                Create a free account · Upgrade to unlock Studio
                            </Typography>
                        </Box>

                        {/* Features */}
                        <Box sx={{ paddingX: '20px', paddingTop: '18px', paddingBottom: '20px', display: 'flex', flexDirection: 'column', gap: '18px', flex: 1 }}>
                            <PricingFeatureLine text="Unlimited filler speech & pause removal" />
                            <PricingFeatureLine text="Edit recordings by transcript or timeline" />
                            <PricingFeatureLine text="Multitrack editing for interviews & podcasts" />
                            <PricingFeatureLine text="Background noise removal & speech enhancement with Auphonic" />
                            <PricingFeatureLine text="Unlimited uploads & exports"/>
                        </Box>
                    </Box>
                </Box>

                {/* Studio Pro card */}
                <Box sx={{ display: 'none', flex: { md: 1 } }}>
                    <Box
                        sx={{
                            width: '100%',
                            height: '100%',
                            backgroundColor: 'var(--landing-surface-muted, #F6F5F4)',
                            borderRadius: '28px',
                            border: '1px solid var(--landing-border-subtle, rgba(0,0,0,0.03))',
                            overflow: 'hidden',
                            padding: '4px',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        {/* Top (white) */}
                        <Box
                            sx={{
                                backgroundColor: 'var(--landing-surface, white)',
                                borderTopLeftRadius: '24px',
                                borderTopRightRadius: '24px',
                                borderBottomLeftRadius: '8px',
                                borderBottomRightRadius: '8px',
                                paddingTop: '20px',
                                paddingX: '24px',
                                paddingBottom: '18px',
                                textAlign: 'center',
                            }}
                        >
                            <Typography
                                sx={{
                                    fontFamily: '"Space Mono", monospace',
                                    fontWeight: 700,
                                    fontSize: '15px',
                                    lineHeight: '24px',
                                    letterSpacing: '-0.8px',
                                    textTransform: 'uppercase',
                                    color: 'var(--landing-text-primary, black)',
                                }}
                            >
                                Studio Pro
                            </Typography>
                            <Typography
                                sx={{
                                    mt: 1.5,
                                    fontFamily: '"Inter", sans-serif',
                                    fontSize: { xs: '16px', md: '18px' },
                                    lineHeight: { xs: '22px', md: '26px' },
                                    letterSpacing: '-0.5px',
                                    color: 'var(--landing-text-primary, black)',
                                    opacity: 0.7,
                                    px: 1,
                                }}
                            >
                                Everything in Studio, with collaboration and team access.
                            </Typography>

                            <Box sx={{ mt: 3, display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: '6px' }}>
                                <Typography
                                    sx={{
                                        fontFamily: '"Hedvig Letters Serif", serif',
                                        fontSize: '52px',
                                        lineHeight: '52px',
                                        letterSpacing: '-2.5px',
                                        color: 'var(--landing-text-primary, black)',
                                    }}
                                >
                                    $49
                                </Typography>
                                <Typography
                                    sx={{
                                        fontFamily: '"Hedvig Letters Serif", serif',
                                        fontSize: '32px',
                                        lineHeight: '32px',
                                        letterSpacing: '-2.5px',
                                        color: 'var(--landing-text-primary, black)',
                                    }}
                                >
                                    /mo
                                </Typography>
                            </Box>

                            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                                <Button
                                    variant="contained"
                                    onClick={() => {
                                        handleSignupFormOpen({librettoPlan: studioProSignupPlan});
                                        trackLandingEvent("landing_cta_click_pricing_studio_pro_get_started");
                                    }}
                                    sx={{
                                        width: '277px',
                                        height: '36px',
                                        backgroundColor: 'var(--landing-surface, white)',
                                        color: 'var(--landing-text-primary, black)',
                                        borderRadius: '999px',
                                        textTransform: 'uppercase',
                                        fontFamily: '"Space Mono", monospace',
                                        fontWeight: 700,
                                        fontSize: '15px',
                                        lineHeight: '24px',
                                        letterSpacing: '-0.8px',
                                        border: '1px solid rgba(0,0,0,0.15)',
                                        boxShadow: '0px 1px 4px rgba(0,0,0,0.08)',
                                        transition: 'background-color 200ms ease, transform 200ms ease, box-shadow 200ms ease',
                                        '&:hover': {
                                            backgroundColor: 'var(--landing-surface-muted, #F6F5F4)',
                                            transform: 'translateY(-1px)',
                                            boxShadow: '0px 3px 8px rgba(0,0,0,0.12)',
                                        },
                                    }}
                                >
                                    Get Started
                                </Button>
                            </Box>

                            <Typography
                                sx={{
                                    mt: 1.5,
                                    fontFamily: '"Inter", sans-serif',
                                    fontSize: '13px',
                                    lineHeight: '18px',
                                    letterSpacing: '-0.2px',
                                    color: 'var(--landing-text-primary, black)',
                                    opacity: 0.7,
                                }}
                            >
                                Create a free account · Upgrade to unlock Studio Pro
                            </Typography>
                        </Box>

                        {/* Features */}
                        <Box sx={{ paddingX: '20px', paddingTop: '18px', paddingBottom: '20px', display: 'flex', flexDirection: 'column', gap: '18px', flex: 1 }}>
                            <PricingFeatureLine text="Everything in Studio" />
                            <PricingFeatureLine text="Team workspaces" />
                            <PricingFeatureLine text="Invite team members as viewers or editors" />
                            <PricingFeatureLine text="Publish and share project pages" />
                            <PricingFeatureLine text="Priority support with SLA" />
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

const FaqChevronIcon = ({open}: {open: boolean}) => (
    <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        style={{
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 200ms ease',
            display: 'block',
        }}
    >
        <path d="M5 8L10 13L15 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);


const FAQ_ITEMS: Array<{question: string; answer: string}> = [
    {
        question: "Can I record on Libretto?",
        answer: "Yes. Record solo with your camera, microphone, or screen, or invite remote guests to a high-quality session. Each participant is captured locally on a separate track, with video quality up to 4K, and the recording opens in the editor when the session is finished.",
    },
    {
        question: "Can I collaborate with an editor or producer?",
        answer: "Yes. Share access to your projects so collaborators can open and work on the same episode in Libretto. Invite people as editors or viewers so they can review or update the project from the same workspace.",
    },
    {
        question: "How does transcript editing work?",
        answer: "Edit your episode by editing the transcript. When you delete words or sentences, the corresponding audio or video is cut from the timeline. You can review the result immediately, then use the timeline for smaller finishing edits.",
    },
    {
        question: "Can I remove filler words and long pauses automatically?",
        answer: "Yes. Libretto can automatically remove filler speech like “um” and “uh” and tighten long pauses in one pass. After cleanup runs, you can review every change in the transcript or timeline and restore anything if needed.",
    },
    {
        question: "Is Libretto a Descript alternative?",
        answer: "Yes. Libretto is focused on helping creators record, edit, clean up, and finish podcasts, interviews, and talking-head videos in one workspace. Transcript editing is the approachable starting point, with timeline controls available for finer edits."
    },
    {
        question: "What types of recordings can I upload?",
        answer: "You can upload audio or video files from any source: Zoom, Riverside, screen recordings, or exports from tools like Premiere or Final Cut. Libretto supports both single-file recordings and multitrack interviews, so you can clean up and edit whatever format you already have.",
    },
    {
        question: "Can I continue editing after cleanup or export elsewhere?",
        answer: "Yes. Cleanup is just the starting point. You can continue refining your edit in Libretto using transcript or timeline editing, or export your cleaned file to use in other tools like Premiere or Final Cut.",
    }
];

const FAQSection = () => {
    const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);

    return (
        <Box sx={{ width: '100%' }}>
            <Box sx={{ textAlign: 'center', mb: { xs: '20px', md: '40px' } }}>
                <Typography
                    sx={{
                        fontFamily: 'Arial, sans-serif',
                        fontWeight: 700,
                        fontSize: { xs: '28px', md: '40px' },
                        lineHeight: 1.2,
                        letterSpacing: { xs: '-1px', md: '-1.5px' },
                        color: 'var(--landing-text-primary, black)',
                        mb: { xs: 2, md: 2.5 },
                        maxWidth: { xs: '327px', md: 'none' },
                        mx: 'auto',
                    }}
                >
                    Common questions
                </Typography>
            </Box>

            <Box sx={{ maxWidth: { xs: '343px', md: '712px', lg: '822px' }, mx: 'auto' }}>
                {FAQ_ITEMS.map((faq, i) => {
                    const isOpen = openFaqIndex === i;
                    const isLast = i === FAQ_ITEMS.length - 1;
                    const nextIsOpen = openFaqIndex === i + 1;

                    return (
                        <Box
                            key={i}
                            sx={{
                                py: '4px',
                                borderBottom: !isOpen && !isLast && !nextIsOpen ? '1px solid var(--landing-divider, #E8DDD4)' : '1px solid transparent',
                            }}
                        >
                            <Box
                                sx={{
                                    backgroundColor: isOpen ? 'var(--landing-surface-muted, #F6F5F4)' : 'transparent',
                                    borderRadius: isOpen ? { xs: '20px', md: '28px' } : 0,
                                    border: isOpen ? '1px solid var(--landing-border-subtle, rgba(0,0,0,0.03))' : '1px solid transparent',
                                    px: { xs: '16px', md: '24px' },
                                    py: { xs: '12px', md: '16px' },
                                    transition: 'background-color 200ms ease, border-color 200ms ease',
                                }}
                            >
                                <Box
                                    component="button"
                                    type="button"
                                    onClick={() => {
                                        const nextOpenIndex = isOpen ? null : i;
                                        setOpenFaqIndex(nextOpenIndex);
                                        if (nextOpenIndex === i) trackLandingEvent(`landing_faq_open_${i}`);
                                    }}
                                    aria-expanded={isOpen}
                                    aria-controls={`faq-panel-${i}`}
                                    sx={{
                                        width: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        textAlign: 'left',
                                        backgroundColor: 'transparent',
                                        border: 0,
                                        padding: 0,
                                        cursor: 'pointer',
                                        '&:focus-visible': {
                                            outline: '2px solid var(--landing-focus, rgba(0,0,0,0.3))',
                                            outlineOffset: '4px',
                                            borderRadius: '8px',
                                        },
                                    }}
                                >
                                    <Typography
                                        component="h3"
                                        sx={{
                                            fontFamily: '"Inter", sans-serif',
                                            fontWeight: 400,
                                            fontSize: { xs: '16px', md: '20px' },
                                            lineHeight: { xs: '22px', md: '28px' },
                                            letterSpacing: '-0.5px',
                                            color: 'var(--landing-text-primary, black)',
                                        }}
                                    >
                                        {faq.question}
                                    </Typography>

                                    <Box
                                        sx={{
                                            width: { xs: '32px', md: '36px' },
                                            height: { xs: '32px', md: '36px' },
                                            backgroundColor: 'var(--landing-surface, white)',
                                            border: '1px solid var(--landing-border-subtle, rgba(0,0,0,0.07))',
                                            borderRadius: '999px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flex: '0 0 auto',
                                            ml: { xs: '12px', md: '16px' },
                                        }}
                                    >
                                        <FaqChevronIcon open={isOpen} />
                                    </Box>
                                </Box>

                                <Collapse in={isOpen} timeout={250}>
                                    <Typography
                                        id={`faq-panel-${i}`}
                                        sx={{
                                            mt: { xs: 1.5, md: 2 },
                                            fontFamily: '"Inter", sans-serif',
                                            fontSize: { xs: '13px', md: '18px' },
                                            lineHeight: { xs: '18px', md: '27px' },
                                            letterSpacing: '-0.5px',
                                            color: 'var(--landing-text-primary, black)',
                                            opacity: 0.7,
                                            pr: { xs: '48px', md: '56px' },
                                        }}
                                    >
                                        {faq.answer}
                                    </Typography>
                                </Collapse>
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
};

// "Who this is for" section
const AudienceSection = () => (
    <Box
        sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            maxWidth: '900px',
            mx: 'auto',
        }}
    >
        <Typography
            sx={{
                fontFamily: '"Arial", sans-serif',
                fontWeight: 700,
                fontSize: { xs: '28px', md: '40px' },
                lineHeight: 1.2,
                letterSpacing: '-1px',
                color: 'var(--landing-text-primary, black)',
                textAlign: 'center',
                mb: { xs: '28px', md: '40px' },
            }}
        >
            Is Libretto right for you?
        </Typography>

        <Box
            sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: { xs: '32px', md: '48px' },
                width: '100%',
            }}
        >
            {/* For you */}
            <Box sx={{ flex: 1 }}>
                <Typography
                    sx={{
                        fontFamily: '"Inter", sans-serif',
                        fontWeight: 600,
                        fontSize: { xs: '18px', md: '20px' },
                        lineHeight: 1.3,
                        letterSpacing: '-0.3px',
                        color: 'var(--landing-text-primary, black)',
                        mb: '20px',
                    }}
                >
                    Built for you if you...
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {[
                        'Edit full podcast episodes and interviews',
                        'Create talking-head or multi-camera videos',
                        'Want transcript-based editing without per-minute pricing',
                        'Clean up audio and tighten delivery',
                    ].map((item) => (
                        <Box
                            component="li"
                            key={item}
                            sx={{
                                fontFamily: '"Inter", sans-serif',
                                fontSize: { xs: '15px', md: '16px' },
                                lineHeight: 1.6,
                                color: 'var(--landing-text-60, rgba(0,0,0,0.6))',
                            }}
                        >
                            {item}
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* Not for you */}
            <Box sx={{ flex: 1 }}>
                <Typography
                    sx={{
                        fontFamily: '"Inter", sans-serif',
                        fontWeight: 600,
                        fontSize: { xs: '18px', md: '20px' },
                        lineHeight: 1.3,
                        letterSpacing: '-0.3px',
                        color: 'var(--landing-text-primary, black)',
                        mb: '20px',
                    }}
                >
                    Probably not for you if you...
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {[
                        'Mainly create short-form clips for TikTok or Reels',
                        'Need fully AI-generated content or synthetic voiceovers',
                        'Want a full motion-graphics or VFX editor like Premiere',
                        'Are looking for a DAW or music production tool',
                    ].map((item) => (
                        <Box
                            component="li"
                            key={item}
                            sx={{
                                fontFamily: '"Inter", sans-serif',
                                fontSize: { xs: '15px', md: '16px' },
                                lineHeight: 1.6,
                                color: 'var(--landing-text-40, rgba(0,0,0,0.4))',
                            }}
                        >
                            {item}
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    </Box>
);

// Section 2: Quiet reassurance
const ReassuranceSection = ({handleSignupFormOpen}: {handleSignupFormOpen: (opts?: any) => void}) => (
    <Box
        sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            pt: { xs: 4, md: 6 },
            pb: { xs: 5, md: 8 },
            px: { xs: 3, md: 4 },
            maxWidth: 700,
            mx: 'auto',
        }}
    >
        <Typography
            sx={{
                fontFamily: 'Arial, sans-serif',
                fontWeight: 700,
                fontSize: { xs: '28px', sm: '40px' },
                lineHeight: 1.2,
                letterSpacing: '-1px',
                color: 'var(--landing-text-primary, black)',
                mb: 1.5,
            }}
        >
            Create your account, no credit card required
        </Typography>
        <Typography
            sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: { xs: '15px', sm: '17px' },
                lineHeight: 1.5,
                letterSpacing: '-0.3px',
                color: 'var(--landing-text-55, rgba(0,0,0,0.55))',
            }}
        >
            Create a free account, then choose a plan to unlock recording and editing.
        </Typography>
        <Button
            variant="contained"
            sx={{
                backgroundColor: 'var(--landing-action-bg, black)',
                color: 'var(--landing-action-text, white)',
                height: {xs: '52px', md: '56px'},
                borderRadius: '14px',
                padding: {xs: '14px 40px', md: '16px 48px'},
                textTransform: 'none',
                fontFamily: '"Inter", sans-serif',
                fontWeight: 600,
                fontSize: {xs: '16px', md: '18px'},
                minWidth: "180px",
                lineHeight: '24px',
                letterSpacing: '-0.3px',
                boxShadow: '0px 1px 4px 0px rgba(0,0,0,0.25)',
                whiteSpace: 'nowrap',
                marginTop: "12px",
                zIndex: 1,
                transition: 'background-color 200ms ease, transform 200ms ease, box-shadow 200ms ease',
                '&:hover': {
                    backgroundColor: 'var(--landing-action-hover, #333)',
                    transform: 'translateY(-1px)',
                    boxShadow: '0px 3px 8px 0px rgba(0,0,0,0.3)',
                },
            }}
            onClick={(e) => {
                handleSignupFormOpen(e);
                trackLandingEvent("landing_cta_click_reassurance_get_started");
            }}
        >
            Create your account
        </Button>
    </Box>
);

const FooterLink = ({label, href, onClick}: {label: string; href?: string; onClick?: () => void}) => {
    return (
        <Button
            component={href ? "a" : "button"}
            href={href}
            onClick={onClick}
            variant="text"
            disableRipple
            sx={{
                minWidth: 'auto',
                padding: 0,
                textTransform: 'none',
                fontFamily: '"Inter", sans-serif',
                fontSize: { xs: '13px', md: '15px', lg: '16px' },
                lineHeight: { xs: '22px', md: '24px', lg: '26px' },
                letterSpacing: '-0.5px',
                color: 'var(--landing-text-primary, black)',
                opacity: 0.7,
                '&:hover': {
                    backgroundColor: 'transparent',
                    opacity: 1,
                },
            }}
        >
            {label}
        </Button>
    );
};

const LandingFooter = ({appearance}: {appearance: SpeechEditorLandingAppearance}) => {
    const navigate = useNavigate();

    return (
        <Box
            component="footer"
            sx={{
                backgroundColor: 'var(--landing-surface-muted, #F6F5F4)',
                width: '100%',
                pt: { xs: '24px', md: '48px' },
                pb: { xs: '24px', md: '40px' },
            }}
        >
            <Box
                sx={{
                    maxWidth: 1440,
                    mx: 'auto',
                    px: { xs: 2, md: 3.5 },
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        alignItems: { xs: 'center', md: 'flex-start' },
                        justifyContent: 'space-between',
                        gap: { xs: 3, md: 6 },
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: { xs: 'center', md: 'flex-start' },
                            textAlign: { xs: 'center', md: 'left' },
                            gap: { xs: 1.5, md: 2 },
                        }}
                    >
                        <Box sx={{ display: 'flex' }}>
                            <NewLogoIconWithText onDark={appearance === "dark"} />
                        </Box>
                        <Typography
                            sx={{
                                fontFamily: '"Inter", sans-serif',
                                fontSize: { xs: '15px', md: '16px', lg: '18px' },
                                lineHeight: { xs: '24px', md: '24px', lg: '26px' },
                                letterSpacing: '-0.5px',
                                color: 'var(--landing-text-primary, black)',
                                opacity: 0.7,
                                maxWidth: { xs: '327px', md: '380px', lg: '520px' },
                            }}
                        >
                            Record, edit, and finish podcasts, interviews, and talking-head videos in one workspace.
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: { xs: 'center', md: 'flex-end' },
                            flexWrap: { xs: 'wrap', md: 'wrap', lg: 'nowrap' },
                            gap: { xs: '16px', md: '20px', lg: '28px' },
                        }}
                    >
                        <FooterLink label="FAQ" href="#faq" />
                        <FooterLink label="Contact" href="mailto:contact@libretto.fm" />
                        <FooterLink label="Terms" onClick={() => navigate('/terms')} />
                        <FooterLink label="Privacy" onClick={() => navigate('/privacy')} />
                    </Box>
                </Box>

                <Box
                    sx={{
                        mt: { xs: 3, md: 4 },
                        pt: { xs: 3, md: 3.5 },
                        borderTop: '1px solid var(--landing-border-subtle, rgba(0, 0, 0, 0.08))',
                        display: 'flex',
                        justifyContent: { xs: 'center', md: 'flex-start' },
                    }}
                >
                    <Typography
                        sx={{
                            fontFamily: '"Inter", sans-serif',
                            fontSize: { xs: '12px', md: '15px', lg: '16px' },
                            lineHeight: { xs: '18px', md: '24px', lg: '26px' },
                            letterSpacing: '-0.5px',
                            color: 'var(--landing-text-primary, black)',
                            opacity: 0.6,
                            textAlign: { xs: 'center', md: 'left' },
                        }}
                    >
                        © 2026 Libretto. All rights reserved.
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

const V2SpeechEditorLandingPage = ({appearance = "light"}: {appearance?: SpeechEditorLandingAppearance}) => {
    const isMobile = useMediaQuery("(max-width:1150px)");
    const authContext = useContext(AuthContext);
    const location = useLocation();
    const navigate = useNavigate();
    const howItWorksPrimarySectionRef = React.useRef<HTMLElement | null>(null);
    const howItWorksSecondarySectionRef = React.useRef<HTMLElement | null>(null);
    const howItWorksTertiarySectionRef = React.useRef<HTMLElement | null>(null);
    const productDemoSectionRef = React.useRef<HTMLElement | null>(null);
    const [activeHowItWorksVideoSection, setActiveHowItWorksVideoSection] = useState<HowItWorksVideoSectionId | null>(null);
    const [demoFormOpen, setDemoFormOpen] = useState(false);
    const landingPath = "/";
    const loginPath = "/login";
    const signupPath = "/signup";
    const isLoginRoute = location.pathname === loginPath;
    const isSignupRoute = location.pathname === signupPath;
    const isAuthModalRoute = isLoginRoute || isSignupRoute;
    const loginDialogOpen = authContext.loginFormOpen || (isLoginRoute && !authContext.signupFormOpen);
    const signupDialogOpen = authContext.signupFormOpen || (isSignupRoute && !authContext.loginFormOpen);

    // Set page title and meta description for landing page
    useEffect(() => {
        document.title = "Libretto | Record, edit, and finish your episode.";
        const meta = document.querySelector('meta[name="description"]');
        if (meta) {
            meta.setAttribute("content", "Record solo or with guests, edit audio and video through the transcript, clean up your sound, and finish your episode in one workspace.");
        }
        trackLandingEvent("landing_view");
    }, []);

    useEffect(() => {
        const sections: Array<{id: HowItWorksVideoSectionId; element: HTMLElement | null}> = [
            { id: HOW_IT_WORKS_VIDEO_SECTIONS.primary, element: howItWorksPrimarySectionRef.current },
            { id: HOW_IT_WORKS_VIDEO_SECTIONS.secondary, element: howItWorksSecondarySectionRef.current },
            { id: HOW_IT_WORKS_VIDEO_SECTIONS.productDemo, element: productDemoSectionRef.current },
            { id: HOW_IT_WORKS_VIDEO_SECTIONS.tertiary, element: howItWorksTertiarySectionRef.current },
        ];

        const updateActiveSection = () => {
            const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
            const viewportCenter = viewportHeight / 2;

            const visibleSections = sections
                .map((section) => {
                    if (!section.element) return null;

                    const rect = section.element.getBoundingClientRect();
                    if (rect.height <= 0) return null;

                    const visibleHeight = Math.max(0, Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0));
                    if (visibleHeight <= 0) return null;

                    const sectionCenter = rect.top + rect.height / 2;
                    return {
                        id: section.id,
                        distanceFromViewportCenter: Math.abs(sectionCenter - viewportCenter),
                        visibleRatio: visibleHeight / rect.height,
                    };
                })
                .filter((section): section is {
                    id: HowItWorksVideoSectionId;
                    distanceFromViewportCenter: number;
                    visibleRatio: number;
                } => section !== null);

            if (visibleSections.length === 0) {
                setActiveHowItWorksVideoSection((prevSection) => (prevSection === null ? prevSection : null));
                return;
            }

            visibleSections.sort((sectionA, sectionB) => {
                if (sectionA.distanceFromViewportCenter === sectionB.distanceFromViewportCenter) {
                    return sectionB.visibleRatio - sectionA.visibleRatio;
                }
                return sectionA.distanceFromViewportCenter - sectionB.distanceFromViewportCenter;
            });

            const nextSection = visibleSections[0].id;
            setActiveHowItWorksVideoSection((prevSection) => (prevSection === nextSection ? prevSection : nextSection));
        };

        let frameId: number | null = null;
        const scheduleUpdate = () => {
            if (frameId !== null) return;
            frameId = window.requestAnimationFrame(() => {
                frameId = null;
                updateActiveSection();
            });
        };

        scheduleUpdate();
        window.addEventListener('scroll', scheduleUpdate, {passive: true});
        window.addEventListener('resize', scheduleUpdate);

        return () => {
            if (frameId !== null) {
                window.cancelAnimationFrame(frameId);
            }
            window.removeEventListener('scroll', scheduleUpdate);
            window.removeEventListener('resize', scheduleUpdate);
        };
    }, []);

    useEffect(() => {
        const scrollToHash = () => {
            const hash = String(window.location.hash || "").trim();
            if (!hash.startsWith("#")) return;
            const targetId = decodeURIComponent(hash.slice(1));
            if (!targetId) return;
            const target = document.getElementById(targetId);
            if (!target) return;
            target.scrollIntoView({behavior: "smooth", block: "start"});
        };

        const timeoutId = window.setTimeout(scrollToHash, 0);
        window.addEventListener("hashchange", scrollToHash);
        return () => {
            window.clearTimeout(timeoutId);
            window.removeEventListener("hashchange", scrollToHash);
        };
    }, []);

    useEffect(() => {
        if (isLoginRoute) {
            authContext.handleSignupFormClose();
            authContext.handleLoginFormOpen();
            return;
        }

        if (isSignupRoute) {
            authContext.handleLoginFormClose();
            authContext.handleSignupFormOpen({
                librettoPlan: resolveSignupPlanFromSearch(location.search),
            });
        }
    }, [authContext, isLoginRoute, isSignupRoute, location.search]);

    const pageStyle = {
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        backgroundColor: "var(--landing-canvas, #FFFFFF)",
        colorScheme: appearance === "dark" ? "dark" : "light",
        ...LANDING_APPEARANCE_VARS[appearance],
    };

    const openLoginModal = () => {
        authContext.handleSignupFormClose();
        authContext.handleLoginFormOpen();
        if (!isLoginRoute) {
            navigate(loginPath, {replace: isAuthModalRoute});
        }
    };

    const openSignupModal = (options?: any) => {
        const librettoPlan = resolveSignupPlanFromOptions(options);
        const nextSignupPath = `${signupPath}?plan=${signupPlanQueryValueForPlan(librettoPlan)}`;
        authContext.handleLoginFormClose();
        authContext.handleSignupFormOpen({librettoPlan});
        if (!isSignupRoute || location.search !== `?plan=${signupPlanQueryValueForPlan(librettoPlan)}`) {
            navigate(nextSignupPath, {replace: isAuthModalRoute});
        }
    };

    const closeLoginModal = () => {
        authContext.handleLoginFormClose();
        if (isAuthModalRoute) {
            navigate(landingPath, {replace: true});
        }
    };

    const closeSignupModal = () => {
        authContext.handleSignupFormClose();
        if (isAuthModalRoute) {
            navigate(landingPath, {replace: true});
        }
    };

    const closeSignupOpenLogin = () => {
        authContext.handleSignupFormClose();
        authContext.handleLoginFormOpen();
        navigate(loginPath, {replace: isAuthModalRoute});
    };

    return (
        <Box sx={pageStyle}>
            {/* HEADER + MODALS + HERO live inside the flex-growing middle */}
            <Box
                sx={{
                    flexGrow: 1,
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        position: "sticky",
                        top: 0,
                        zIndex: 100,
                        width: "100%",
                        backgroundColor: 'var(--landing-header-bg, rgba(255,255,255,0.85))',
                        backdropFilter: 'blur(12px)',
                        WebkitBackdropFilter: 'blur(12px)',
                        display: "flex",
                        justifyContent: "center",
                    }}
                >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        width: "100%",
                        maxWidth: 1450,
                        padding: "20px",
                        position: "relative",
                    }}
                >
                    <Box
                        sx={{ display: "flex", cursor: "pointer" }}
                        onClick={() => {}}
                    >
                        <NewLogoIconWithText onDark={appearance === "dark"} />
                    </Box>

                    {!isMobile && (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: { md: "24px", lg: "32px" },
                                position: "absolute",
                                left: "50%",
                                transform: "translateX(-50%)",
                            }}
                        >
                            {/* <V2TopBarNavLink label="Features" href="#features" /> */}
                            {/* <V2TopBarNavLink label="How it Works" href="#how-it-works" /> */}
                            {/* <V2TopBarNavLink label="Pricing" href="#pricing" /> */}
                            {/* <V2TopBarNavLink label="FAQ" href="#faq" /> */}
                        </Box>
                    )}

                    <Box sx={{ display: "flex", alignItems: "center", gap: "12px", marginLeft: "auto" }}>
                        <V2SigninButton
                            onClick={() => {
                                openLoginModal();
                                trackLandingEvent("landing_cta_click_topbar_sign_in");
                            }}
                        />
                        <V2GetStartedButton
                            onClick={(e) => {
                                openSignupModal(e);
                                trackLandingEvent("landing_cta_click_topbar_get_started");
                            }}
                        />
                    </Box>

                    {/* Login dialog */}
                    <Dialog
                        open={loginDialogOpen}
                        onClose={closeLoginModal}
                        aria-labelledby="form-dialog-title"
                        sx={{
                            "& .MuiDialog-paper": {
                                borderRadius: "20px",
                            },
                        }}
                    >
                        <DialogContent
                            sx={{
                                borderRadius: "30px",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                minWidth: { xs: "300px", md: "400px" },
                            }}
                        >
                            <V2LoginForm
                                signinWithGoogle={authContext.signinWithGoogle}
                                handleLoginFormClose={closeLoginModal}
                                signinWithEmail={authContext.signinWithEmail}
                                handleSignUpFormOpen={openSignupModal}
                                sendPasswordResetEmailForUser={
                                    authContext.sendPasswordResetEmailForUser
                                }
                            />
                        </DialogContent>
                    </Dialog>

                    {/* Signup message dialog */}
                    <Dialog
                        open={authContext.signUpStateMessageOpen}
                        onClose={authContext.handleSignupStateMessageClose}
                        aria-labelledby="form-dialog-title"
                    >
                        <DialogContent
                            sx={{
                                borderRadius: "30px",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                width: { xs: "300px", md: "400px" },
                            }}
                        >
                            <V2SignupMessage
                                signUpStateMessage={authContext.signUpStateMessage}
                            />
                        </DialogContent>
                    </Dialog>

                    {/* Signup dialog */}
                    <Dialog
                        open={signupDialogOpen}
                        onClose={closeSignupModal}
                        aria-labelledby="form-dialog-title"
                        sx={{
                            "& .MuiDialog-paper": {
                                borderRadius: "20px",
                            },
                        }}
                    >
                        <DialogContent
                            sx={{
                                borderRadius: "30px",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                minWidth: { xs: "300px", md: "400px" },
                            }}
                        >
                            <V2SignupForm
                                selectedPricingOption={"Annual"}
                                signupWithGoogle={authContext.signupWithGoogle}
                                closeSignupOpenLogin={closeSignupOpenLogin}
                                signUpWithEmailAndPassword={
                                    authContext.signUpWithEmailAndPassword
                                }
                            />
                        </DialogContent>
                    </Dialog>

                    {/* Demo request dialog */}
                    <DemoRequestDialog
                        open={demoFormOpen}
                        onClose={() => setDemoFormOpen(false)}
                        handleSignupFormOpen={openSignupModal}
                    />
                </Box>
                </Box>

                {/* Hero section */}
                <Box
                    sx={{
                        width: "100%",
                        px: { xs: 2, md: 4 },
                        pt: { xs: 1, md: 2 },
                        pb: 0,
                    }}
                >
                    <HeroSection
                        isMobile={isMobile}
                        handleLoginFormOpen={openLoginModal}
                        handleSignupFormOpen={openSignupModal}
                    />
                </Box>

                {/* Section 1: How Libretto works */}
                {/*<Box*/}
                {/*    component="section"*/}
                {/*    id="how-it-works"*/}
                {/*    ref={howItWorksPrimarySectionRef}*/}
                {/*    sx={{*/}
                {/*        width: "100%",*/}
                {/*        mt: { xs: '-24px', md: '-32px' },*/}
                {/*        pt: { xs: '64px', md: '96px' },*/}
                {/*        pb: { xs: '32px', md: '64px' },*/}
                {/*        backgroundColor: 'var(--landing-surface-soft, #FAFAFA)',*/}
                {/*        borderTopLeftRadius: { xs: '48px', md: '72px' },*/}
                {/*        borderTopRightRadius: { xs: '48px', md: '72px' },*/}
                {/*        borderBottomLeftRadius: { xs: '48px', md: '72px' },*/}
                {/*        borderBottomRightRadius: { xs: '48px', md: '72px' },*/}
                {/*        borderTop: '1px solid var(--landing-border-subtle, rgba(0,0,0,0.04))',*/}
                {/*        overflow: 'hidden',*/}
                {/*        scrollMarginTop: '90px',*/}
                {/*    }}*/}
                {/*>*/}
                {/*    <Box id={HOW_IT_WORKS_ANCHORS.cleanup} sx={{scrollMarginTop: '90px'}} />*/}
                {/*    <Box*/}
                {/*        sx={{*/}
                {/*            maxWidth: 1440,*/}
                {/*            mx: 'auto',*/}
                {/*            px: { xs: 2, md: 3.5 },*/}
                {/*        }}*/}
                {/*    >*/}
                {/*        <HowItWorksSection shouldPlayVideo={activeHowItWorksVideoSection === HOW_IT_WORKS_VIDEO_SECTIONS.primary} handleSignupFormOpen={openSignupModal} />*/}
                {/*    </Box>*/}
                {/*</Box>*/}

                {/*/!* Section 2: Edit like a document *!/*/}
                {/*<Box*/}
                {/*    component="section"*/}
                {/*    id={HOW_IT_WORKS_ANCHORS.editByTranscript}*/}
                {/*    ref={howItWorksSecondarySectionRef}*/}
                {/*    sx={{*/}
                {/*        width: "100%",*/}
                {/*        py: { xs: '40px', md: '80px' },*/}
                {/*        scrollMarginTop: '90px',*/}
                {/*        backgroundColor: 'var(--landing-surface, white)',*/}
                {/*    }}*/}
                {/*>*/}
                {/*    <Box*/}
                {/*        sx={{*/}
                {/*            maxWidth: 1440,*/}
                {/*            mx: 'auto',*/}
                {/*            px: { xs: 2, md: 3.5 },*/}
                {/*        }}*/}
                {/*    >*/}
                {/*        <HowItWorksSectionTwo shouldPlayVideo={activeHowItWorksVideoSection === HOW_IT_WORKS_VIDEO_SECTIONS.secondary} handleSignupFormOpen={openSignupModal} />*/}
                {/*    </Box>*/}
                {/*</Box>*/}

                {/* Product Demo Section with tabbed video demos */}
                <Box
                    component="section"
                    id="how-it-works"
                    ref={productDemoSectionRef}
                    sx={{
                        width: "100%",
                        pt: { xs: '32px', md: '56px' },
                        pb: { xs: '32px', md: '48px' },
                        scrollMarginTop: '90px',
                        backgroundColor: 'var(--landing-surface, white)',
                    }}
                >
                    <Box
                        sx={{
                            maxWidth: 1440,
                            mx: 'auto',
                            px: { xs: 2, md: 3.5 },
                        }}
                    >
                        <UseCasesSection isMobile={isMobile} />
                    </Box>
                </Box>

                {/* Feature Showcase Section */}
                <Box
                    component="section"
                    sx={{
                        width: '100%',
                        pt: {xs: '36px', md: '56px'},
                        pb: {xs: '36px', md: '56px'},
                        backgroundColor: 'var(--landing-surface, white)',
                    }}
                >
                    <Box
                        sx={{
                            maxWidth: 1440,
                            mx: 'auto',
                            px: {xs: 2, md: 3.5},
                        }}
                    >
                        <FeatureShowcaseSection
                            shouldPlayVideo={true}
                            isMobile={isMobile}
                        />

                        <Box sx={{mt: {xs: '48px', md: '72px'}}}>
                            <CollaborationCallout />
                        </Box>
                    </Box>
                </Box>

                {/* Pricing Section, hidden for now */}

                {/* Mid-page CTA */}
                <Box
                    sx={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '12px',
                        pt: { xs: '8px', md: '12px' },
                        pb: { xs: '32px', md: '48px' },
                        backgroundColor: 'var(--landing-surface, white)',
                    }}
                >
                    <Typography
                        sx={{
                            fontFamily: '"Inter", sans-serif',
                            fontWeight: 400,
                            fontSize: { xs: '16px', md: '18px' },
                            lineHeight: 1.5,
                            color: 'var(--landing-text-45, rgba(0,0,0,0.45))',
                            textAlign: 'center',
                        }}
                    >
                        Ready to get started?
                    </Typography>
                    <Button
                        variant="contained"
                        sx={{
                            backgroundColor: 'var(--landing-action-bg, black)',
                            color: 'var(--landing-action-text, white)',
                            height: { xs: '52px', md: '56px' },
                            borderRadius: '14px',
                            padding: { xs: '14px 40px', md: '16px 48px' },
                            textTransform: 'none',
                            fontFamily: '"Inter", sans-serif',
                            fontWeight: 600,
                            fontSize: { xs: '16px', md: '18px' },
                            minWidth: '180px',
                            lineHeight: '24px',
                            letterSpacing: '-0.3px',
                            boxShadow: '0px 1px 4px 0px rgba(0,0,0,0.25)',
                            whiteSpace: 'nowrap',
                            transition: 'background-color 200ms ease, transform 200ms ease, box-shadow 200ms ease',
                            '&:hover': {
                                backgroundColor: 'var(--landing-action-hover, #333)',
                                transform: 'translateY(-1px)',
                                boxShadow: '0px 3px 8px 0px rgba(0,0,0,0.3)',
                            },
                        }}
                        onClick={(e) => {
                            openSignupModal(e);
                            trackLandingEvent("landing_cta_click_mid_page");
                        }}
                    >
                        Create your account
                    </Button>
                </Box>

                {/* Who this is for */}
                <Box
                    component="section"
                    sx={{
                        width: '100%',
                        pt: { xs: '32px', md: '48px' },
                        pb: { xs: '32px', md: '48px' },
                        backgroundColor: 'var(--landing-surface, white)',
                    }}
                >
                    <Box
                        sx={{
                            maxWidth: 1440,
                            mx: 'auto',
                            px: { xs: 2, md: 3.5 },
                        }}
                    >
                        <AudienceSection />
                    </Box>
                </Box>

                {/* CTA Band */}
                <Box
                    sx={{
                        width: "100%",
                    }}
                >
                    <ReassuranceSection handleSignupFormOpen={openSignupModal} />
                </Box>

                {/* FAQ */}
                <Box
                    component="section"
                    id="faq"
                    sx={{
                        width: "100%",
                        pt: { xs: '32px', md: '56px' },
                        pb: { xs: '20px', md: '36px' },
                        scrollMarginTop: '90px',
                        backgroundColor: 'var(--landing-surface-soft, #FAFAFA)',
                        borderTopLeftRadius: { xs: '48px', md: '72px' },
                        borderTopRightRadius: { xs: '48px', md: '72px' },
                        borderBottomLeftRadius: { xs: '48px', md: '72px' },
                        borderBottomRightRadius: { xs: '48px', md: '72px' },
                        borderTop: '1px solid var(--landing-border-subtle, rgba(0,0,0,0.04))',
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        sx={{
                            maxWidth: 1440,
                            mx: 'auto',
                            px: { xs: 2, md: 3.5 },
                        }}
                    >
                        <FAQSection />
                    </Box>
                </Box>
            </Box>

            {/* FOOTER */}
            <Box
                sx={{
                    width: "100%",
                }}
            >
                <LandingFooter appearance={appearance} />
            </Box>
        </Box>
    );
};

export default V2SpeechEditorLandingPage;
